import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import axios from 'axios';
import { useAccessibleAccounts, useSignatures, useSendIdentities, useRecentRecipients, useEmail, useEmailTemplateLibrary, saveDraftApi, deleteDraftApi, revalidateEmailDraftCaches, revalidateOutboxCaches, fetchDraftApi, fetchOutboxApi, draftToFormData, templateToFormData, importComposeMessage, getTemplateApi, fetchTemplateAttachmentFile, templateDocumentToFile, updateTemplateApi, canOverwriteLibraryTemplate, type Email, type EmailDraft, type EmailDraftListItem, type EmailOutboxDetail, type EmailTemplateDocument } from './useData';
import { useAddressBookOrganizations, useAddressBookContacts } from '../../hooks/useAddressBook';
import {
  buildComposeRecipientSuggestions,
  collectAddressBookSuggestions,
} from './utils/composeRecipientSuggestions';
import type { EmailTemplate } from './useData';
import { Button, TextInput, Select, ConfirmationDialog, Textarea } from '../../components/ui';
import { AnchoredMenu } from '../../components/ui/AnchoredMenu';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import SaveAsTemplateModal from './components/SaveAsTemplateModal';
import AttachFromTemplateLibraryModal from './components/AttachFromTemplateLibraryModal';
import OpenTemplateLibraryModal from './components/OpenTemplateLibraryModal';
import ComposeFileSendSidebar from './components/ComposeFileSendSidebar';
import { cn } from '../../lib/utils';
import RecipientSelector, { RecipientCcBccSection, RecipientInsetFieldShell } from './components/RecipientSelector';
import { API_BASE_URL } from '../../config/apiConfig';
import ManageSignaturesModal from './components/ManageSignaturesModal';
import EmailAiAssistant from './components/ai-assistant/EmailAiAssistant';
import { useAiEmailsEnabled } from '../ai/useAiAssistant';
import ComposeAddressBookSidebar, { appendRecipientToField } from './components/ComposeAddressBookSidebar';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import EntityCustomFieldsSection from '../custom-fields/EntityCustomFieldsSection';
import { CUSTOM_FIELD_ENTITY } from '../custom-fields/entityTypes';
import { prepareSignatureHtml } from './utils/signatureUtils';
import { parseRecipientFieldForSend, recipientFieldsForPersistence, getInvalidRecipientTokens } from './utils/recipientUtils';
import DOMPurify from 'dompurify';
import { sanitizeEmailBodyForDisplay, sanitizeRichEmailHtml } from './utils/sanitizeEmailBody';
import { emailPartitionAtSearch } from './utils/emailPartitionAtQuery';
import SlateEmailEditor, { type SlateEmailEditorRef } from '../../components/editor/SlateEmailEditor';
import { APP_CONFIG, formatDateTime } from '../../config/appConfig';
import { formatFileSize, formatFileSizeProgress } from '../../components/attachments/fileIconUtils';
import {
  getReplyForwardIntroHtml,
  setReplyForwardIntroHtml,
  resetReplyForwardIntroHtml,
  getReplyForwardIntroRawForEdit,
  DEFAULT_REPLY_FORWARD_INTRO_HTML,
  replaceReplyForwardIntroInBody,
} from './utils/replyForwardIntroStorage';
import { expandComposeEditorHtmlForDisplay } from './utils/unlockReplyForwardEditing';
import { exitCompose, takeComposeInitialUserBody } from './utils/composeNavigation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  emptyComposeDoc,
  templateToComposeDoc,
  importToComposeDoc,
  emailToComposeDoc,
  composeDocToOutbound,
  composeDocToEditorHtml,
} from './composeBody';
import { composeDocFromDraft } from './composeBody/composeDocFromDraft';
import { buildComposeDocForPersistence } from './composeBody/buildComposeDocForPersistence';
import { buildComposeDocForTemplatePersistence } from './composeBody/buildComposeDocForTemplatePersistence';
import { trimTrailingEmptyComposeParagraphs, stripLockedComposeBlocksFromUserBody } from './composeBody/editorHtml';
import { extractPlainTextFromHtml } from './composeBody/htmlToPlainText';
import { importMessageToEditableEditorHtml } from './utils/unlockReplyForwardEditing';
import { QuotedBodyPanel } from './composeBody/QuotedBodyPanel';
import { EMAIL_MESSAGE_CANVAS_CLASS } from './utils/emailMessageCanvas';
import type { ComposeBodyDocument, ComposeBodyKind } from './composeBody/types';
import { getNonEmbeddedAttachments } from './utils/attachments';
import ComposeHeader, { type ComposeSendProgress } from './components/ComposeHeader';
import ComposeMultiWindowHint from './components/ComposeMultiWindowHint';
import ComposeToolRail from './components/ComposeToolRail';
import ComposeSignaturesSidebar from './components/ComposeSignaturesSidebar';
import ComposeScheduleSendModal from './components/ComposeScheduleSendModal';
import { outboxQueuedSuccessMessage, outboxScheduledSuccessMessage } from './utils/outboxMessages';
import { useEmailPresence } from './hooks/useEmailPresence';
import { formatDraftAutosaveCountdown, useComposeDraftAutosave } from './hooks/useComposeDraftAutosave';
import {
  clearComposeSessionDraftId,
  enqueueComposeDraftSave,
  readComposeSessionDraftId,
  startNewComposeSession,
  writeComposeSessionDraftId,
} from './utils/composeDraftSession';
import { useUserSettings } from '../settings/useData';
import {
  applyComposeDefaultsToUserBody,
  buildDefaultComposeUserBodyHtml,
  isEmptyComposeUserBody,
  sanitizeEmailComposeDefaults,
} from './utils/emailComposeDefaults';

const COMPOSE_DRAFT_KEY = 'compose-draft';
const SUBJECT_MAX_LENGTH = 998;
const DEFAULT_ATTACHMENT_SIZE_LIMIT_MB = 25;

function fileToBase64SendAttachment(
  file: File,
  onProgress?: (ratio: number) => void,
  signal?: AbortSignal,
): Promise<{ filename: string; content: string; contentType?: string }> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const reader = new FileReader();
    const onAbort = () => {
      reader.abort();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    reader.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(event.loaded / event.total);
      }
    };
    reader.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      onProgress?.(1);
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve({
        filename: file.name,
        content: comma >= 0 ? result.slice(comma + 1) : result,
        contentType: file.type || undefined,
      });
    };
    reader.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(reader.error ?? new Error('Failed to read file'));
    };
    reader.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    reader.readAsDataURL(file);
  });
}

type SendAttachmentPayload =
  | { filename: string; content: string; contentType?: string }
  | { filename: string; path: string; contentType?: string };

function formatAddrList(arr: Array<{ name?: string; email: string }> | null | undefined): string {
  if (!arr?.length) return '';
  return arr
    .map((a) => (a.name && a.email ? `${a.name} <${a.email}>` : a.email || ''))
    .filter(Boolean)
    .join(', ');
}

function isComposeMessageFile(file: File): boolean {
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
  if (['.eml', '.msg', '.oft'].includes(ext)) return true;
  const mime = (file.type || '').toLowerCase();
  return mime.includes('message/rfc822') || mime.includes('application/vnd.ms-outlook');
}

function base64ToFile(filename: string, contentType: string, base64: string): File {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: contentType || 'application/octet-stream' });
}

/** Wrap signature HTML for compose preview below the editor. */
function htmlIntroToPlain(introHtml: string): string {
  if (!introHtml) return '';
  return extractPlainTextFromHtml(introHtml).replace(/\u00a0/g, ' ');
}

function wrapSignatureHtml(signature: string): string {
  if (!signature) return '';
  const prepared = prepareSignatureHtml(signature);
  return `<div class="email-signature" contenteditable="false" style="display:flow-root;clear:both;max-width:100%;margin-bottom:0.75rem">${prepared}</div>`;
}

function attachAccountSignature(
  doc: ComposeBodyDocument,
  signature: string | undefined,
): ComposeBodyDocument {
  if (!signature?.trim()) {
    return { ...doc, signature: undefined };
  }
  return { ...doc, signature: wrapSignatureHtml(signature) };
}

function hasComposeContentFromDoc(
  formData: { to: string; subject: string; html: string },
  doc: ComposeBodyDocument,
  plainText: string,
  stripSignature: (h: string) => string,
): boolean {
  if (formData.to.trim()) return true;
  if (formData.subject.trim()) return true;
  if (doc.userBody.trim()) return true;
  const bodyText = stripSignature(formData.html || '').trim();
  if (bodyText && bodyText !== '<p><br></p>' && bodyText !== '<p></p>') return true;
  if (plainText.trim()) return true;
  return false;
}

type ComposeSnapshot = {
  accountId: number;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  html: string;
  attachments: string;
};

function makeComposeSnapshot(
  formData: { accountId: number; to: string; cc: string; bcc: string; subject: string; html: string },
  attachmentList: { name: string; size: number }[],
): ComposeSnapshot {
  return {
    accountId: formData.accountId,
    to: formData.to,
    cc: formData.cc,
    bcc: formData.bcc,
    subject: formData.subject,
    html: formData.html,
    attachments: attachmentList.map((a) => `${a.name}:${a.size}`).sort().join('|'),
  };
}

function composeSnapshotsEqual(a: ComposeSnapshot, b: ComposeSnapshot): boolean {
  return (
    a.accountId === b.accountId &&
    a.to === b.to &&
    a.cc === b.cc &&
    a.bcc === b.bcc &&
    a.subject === b.subject &&
    a.html === b.html &&
    a.attachments === b.attachments
  );
}

type RecipientFields = { to: string; cc: string; bcc: string };

type ComposeLoadingState = {
  message: string;
  progress?: number;
};

type ComposeAttachmentItem = { id: string; file: File; name: string; size: number };

async function loadNonEmbeddedEmailAttachments(
  emailId: number,
  partitionAt: string | null | undefined,
  meta: ReturnType<typeof getNonEmbeddedAttachments>,
  idPrefix: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<ComposeAttachmentItem[]> {
  const pq = emailPartitionAtSearch(partitionAt);
  const next: ComposeAttachmentItem[] = [];
  for (let i = 0; i < meta.length; i++) {
    onProgress?.(i + 1, meta.length);
    const { data } = await api.get<Blob>(`/emails/${emailId}/attachments/${i}${pq}`, { responseType: 'blob' });
    const attMeta = meta[i];
    const name = attMeta.filename || `attachment-${i + 1}`;
    const file = new File([data], name, {
      type: attMeta.contentType || data.type || 'application/octet-stream',
    });
    next.push({
      id: `${idPrefix}-${emailId}-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
    });
  }
  return next;
}

const Compose: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editAsNewIdParam = searchParams.get('editAsNew');
  const editAsNewPartitionAtParam = searchParams.get('editAsNewPartitionAt');
  const editAsNewId = editAsNewIdParam ? parseInt(editAsNewIdParam, 10) : null;
  const { data: editAsNewFetchedEmail, isLoading: editAsNewEmailLoading } = useEmail(
    editAsNewId && !(location.state as { editAsNew?: Email } | null)?.editAsNew ? editAsNewId : 0,
    editAsNewPartitionAtParam
  );
  const isReply = location.pathname.includes('/reply');
  const isReplyAll = location.pathname.includes('/reply-all');
  const isForward = location.pathname.includes('/forward');
  const { data: accounts } = useAccessibleAccounts();
  const { accounts: accountsWithSigs, byAccount: signaturesByAccount, mutate: mutateSignatures } = useSignatures();
  const { byAccount: identitiesByAccount } = useSendIdentities();
  const { data: recentRecipientsData } = useRecentRecipients();
  const threadPartitionAt = searchParams.get('partitionAt');
  const { data: originalEmail, isLoading: originalEmailLoading } = useEmail(id ? parseInt(id) : 0, threadPartitionAt);
  const composeEmailId = id ? parseInt(id, 10) : null;
  const presenceUsers = useEmailPresence(
    (isReply || isReplyAll || isForward) && composeEmailId ? composeEmailId : null,
    'replying',
  );
  const { showSuccess, showError } = useNotification();
  const { applicationSettings } = useSettings();
  const { userSettings, isLoading: userSettingsLoading } = useUserSettings();
  const composeDefaults = useMemo(
    () => sanitizeEmailComposeDefaults(userSettings?.emailComposeDefaults),
    [userSettings?.emailComposeDefaults],
  );
  const composeDefaultsReady = !userSettingsLoading && userSettings != null;
  const includeSentHistoryEmails = composeDefaults.includeSentHistoryEmails;
  const includeAddressBookEmails = composeDefaults.includeAddressBookEmails;
  const { organizations: addressBookOrgs } = useAddressBookOrganizations(includeAddressBookEmails);
  const { contacts: addressBookContacts } = useAddressBookContacts(includeAddressBookEmails);
  const enableAiEmails = useAiEmailsEnabled();
  const { can } = useAuth();
  const { company: templateCompanyLibrary, personal: templatePersonalLibrary, shared: templateSharedLibrary, mutate: mutateTemplateLibrary, isLoading: templateLibraryLoading } =
    useEmailTemplateLibrary();
  const canEditCompanyTemplates = can('company_email_templates', 'edit');
  const backButtonRef = useRef<HTMLButtonElement>(null);

  const [composeDoc, setComposeDoc] = useState<ComposeBodyDocument>(() => emptyComposeDoc());
  const composeDocRef = useRef(composeDoc);
  composeDocRef.current = composeDoc;

  const applyComposeDoc = useCallback((doc: ComposeBodyDocument) => {
    setComposeDoc(doc);
    setFormData((prev) => ({ ...prev, html: composeDocToEditorHtml(doc) }));
    setEditorContentKey((k) => k + 1);
  }, []);

  const syncComposeUserBodyHtml = useCallback((html: string) => {
    const userBody = stripLockedComposeBlocksFromUserBody(
      trimTrailingEmptyComposeParagraphs(html, { maxTrailingEmpty: 1 }),
    );
    setComposeDoc((prev) => {
      const incomingEmpty = isEmptyComposeUserBody(userBody);
      const prevEmpty = isEmptyComposeUserBody(prev.userBody);
      // Ignore serializer noise (br vs br/, color hex vs rgb, etc.) on empty placeholders.
      // Re-seeding defaults against that noise caused remount loops / max update depth.
      if (incomingEmpty && prevEmpty && prev.userBody.trim()) {
        return prev;
      }
      // After loading a library template, Slate can emit an empty onChange during remount
      // before nodes hydrate — do not wipe the just-applied body.
      if (
        incomingEmpty &&
        !prevEmpty &&
        (templateEnrichPendingRef.current || Date.now() < ignoreEmptyEditorSyncUntilRef.current)
      ) {
        return prev;
      }
      const next: ComposeBodyDocument = { ...prev, bodyKind: 'rich' as ComposeBodyKind, userBody };
      setFormData((fd) => ({ ...fd, html: composeDocToEditorHtml(next) }));
      return next;
    });
  }, []);

  const [formData, setFormData] = useState({
    accountId: accounts[0]?.id || 0,
    sendIdentityId: undefined as number | undefined,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    html: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<ComposeSendProgress | null>(null);
  const [sendHasAttachments, setSendHasAttachments] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const sendAbortRef = useRef<AbortController | null>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const formDataRef = useRef(formData);
  const recipientsSnapshotRef = useRef<RecipientFields>({ to: '', cc: '', bcc: '' });
  formDataRef.current = { ...formData, ...recipientsSnapshotRef.current };

  const applyRecipientFields = useCallback((patch: Partial<RecipientFields>) => {
    const next: RecipientFields = {
      ...recipientsSnapshotRef.current,
      ...patch,
    };
    recipientsSnapshotRef.current = next;
    formDataRef.current = { ...formDataRef.current, ...next };
    const showCcNext = Boolean(next.cc.trim());
    const showBccNext = Boolean(next.bcc.trim());
    setFormData((prev) => ({ ...prev, ...patch }));
    if (patch.cc !== undefined && showCcNext) setShowCc(true);
    if (patch.bcc !== undefined && showBccNext) setShowBcc(true);
  }, []);

  const resolveRecipientsForPersistence = useCallback(
    () => recipientFieldsForPersistence(recipientsSnapshotRef.current),
    [],
  );

  const handleChangeCc = useCallback(
    (value: string) => {
      applyRecipientFields({ cc: value });
    },
    [applyRecipientFields],
  );

  const handleChangeBcc = useCallback(
    (value: string) => {
      applyRecipientFields({ bcc: value });
    },
    [applyRecipientFields],
  );

  const handleToggleCc = useCallback(() => {
    if (showCc) {
      applyRecipientFields({ cc: '' });
      setShowCc(false);
      return;
    }
    setShowCc(true);
  }, [showCc, applyRecipientFields]);

  const handleToggleBcc = useCallback(() => {
    if (showBcc) {
      applyRecipientFields({ bcc: '' });
      setShowBcc(false);
      return;
    }
    setShowBcc(true);
  }, [showBcc, applyRecipientFields]);
  const [showSignaturesModal, setShowSignaturesModal] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editorContentKey, setEditorContentKey] = useState(0);
    const [editorHeight, setEditorHeight] = useState(420);
  const [touchedTo, setTouchedTo] = useState(false);
  const [touchedSubject, setTouchedSubject] = useState(false);
  const [attachments, setAttachments] = useState<{ id: string; file: File; name: string; size: number }[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const [attachmentDragOver, setAttachmentDragOver] = useState(false);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<ComposeSnapshot | null>(null);
  const [baselinePending, setBaselinePending] = useState(false);
  const [prefillBaselineTick, setPrefillBaselineTick] = useState(0);
  const templateEnrichPendingRef = useRef(false);
  /** Bumps on each template load so stale async results cannot clear a newer load. */
  const templateLoadGenRef = useRef(0);
  /** After applying a template body, ignore empty Slate onChange noise briefly. */
  const ignoreEmptyEditorSyncUntilRef = useRef(0);
  const editAsNewAttachmentsPendingRef = useRef(false);
  const forwardAttachmentsPendingRef = useRef(false);
  const requestPrefillBaseline = useCallback(() => {
    setPrefillBaselineTick((t) => t + 1);
  }, []);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const replyForwardSigInsertedRef = useRef(false);
  const editorHeightMin = 160;
  const editorHeightMax = 1200;
  const resizeRef = useRef({ startY: 0, startHeight: 0 });
  const editorRef = useRef<SlateEmailEditorRef>(null);
  const messageFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!APP_CONFIG.features.fileSend) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; url?: string } | null;
      if (!data || data.type !== 'ucomm-file-send' || typeof data.url !== 'string') return;
      const url = data.url.trim();
      if (!url) return;
      const safeHref = DOMPurify.sanitize(url, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      if (!safeHref.startsWith('http://') && !safeHref.startsWith('https://')) return;
      const html = `<p><a href="${safeHref}">Secure file download</a></p>`;
      editorRef.current?.insertHtml(html);
      const updated = editorRef.current?.getHtml?.();
      if (updated != null) {
        setFormData((prev) => ({ ...prev, html: updated }));
      }
      showSuccess('Encrypted file link inserted into the message.');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [showSuccess]);

  const [composeLoading, setComposeLoading] = useState<ComposeLoadingState | null>(null);
  const [isImportingMessage, setIsImportingMessage] = useState(false);
  const [signaturesPanelOpen, setSignaturesPanelOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [savingLibraryTemplate, setSavingLibraryTemplate] = useState(false);
  const [openedLibraryTemplate, setOpenedLibraryTemplate] = useState<EmailTemplate | null>(null);
  const skipNextSignatureSyncRef = useRef(false);
  const canOverwriteOpenedTemplate = useMemo(
    () => canOverwriteLibraryTemplate(openedLibraryTemplate, canEditCompanyTemplates),
    [openedLibraryTemplate, canEditCompanyTemplates],
  );
  const [libraryAttachOpen, setLibraryAttachOpen] = useState(false);
  const [openLibraryModal, setOpenLibraryModal] = useState(false);
  const [libraryReplaceConfirm, setLibraryReplaceConfirm] = useState<EmailTemplate | null>(null);
  const [fileSendPanelOpen, setFileSendPanelOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [showReplyForwardIntroModal, setShowReplyForwardIntroModal] = useState(false);
  const [showScheduleSendModal, setShowScheduleSendModal] = useState(false);
  const [replyForwardIntroDraft, setReplyForwardIntroDraft] = useState(DEFAULT_REPLY_FORWARD_INTRO_HTML);
  /** Captured on AI button mousedown (before focus moves) so Slate selection is not lost when the drawer opens. */
  const aiSelectionCaptureRef = useRef<string>('');
  const [addressBookOpen, setAddressBookOpen] = useState(false);

  const markComposeSaved = useCallback((override?: {
    formData?: typeof formData;
    attachments?: typeof attachments;
  }) => {
    const fd = override?.formData ?? formDataRef.current;
    const att = override?.attachments ?? attachmentsRef.current;
    setSavedSnapshot(makeComposeSnapshot(fd, att));
    setBaselinePending(false);
  }, []);

  const applySavedRecipientsToForm = useCallback(
    (saved: RecipientFields) => {
      applyRecipientFields(saved);
      markComposeSaved({
        formData: {
          ...formDataRef.current,
          ...saved,
        },
      });
    },
    [applyRecipientFields, markComposeSaved],
  );

  const recentRecipients = useMemo(() => {
    return buildComposeRecipientSuggestions({
      includeSentHistoryEmails,
      includeAddressBookEmails,
      recentRecipients: includeSentHistoryEmails ? recentRecipientsData ?? [] : [],
      addressBookEntries: includeAddressBookEmails
        ? collectAddressBookSuggestions(addressBookOrgs, addressBookContacts)
        : [],
    });
  }, [
    includeSentHistoryEmails,
    includeAddressBookEmails,
    recentRecipientsData,
    addressBookOrgs,
    addressBookContacts,
  ]);

  /** Prefer live editor selection; if drawer stole focus, use text captured when opening AI. */
  const getSelectedTextForAi = useCallback(() => {
    const live = editorRef.current?.getSelectedText?.()?.trim() ?? '';
    if (live) return live;
    return aiSelectionCaptureRef.current?.trim() ?? '';
  }, []);


  const getSignatureForContext = useCallback((accountId: number): string => {
    const acc = signaturesByAccount[accountId];
    if (!acc?.signatures?.length) return '';
    const defaultSig = acc.signatures.find((s) => s.isDefault);
    const sig = defaultSig ?? acc.signatures[0];
    return sig.body ?? '';
  }, [signaturesByAccount]);

  const stripSignature = (html: string): string => {
    if (!html) return '';
    // Remove all signature blocks from the end (loop until none left). Match div with class containing email-signature.
    let out = html.trim();
    let prev = '';
    while (prev !== out) {
      prev = out;
      out = out.replace(/\s*<div[^>]*\bclass="[^"]*email-signature[^"]*"[^>]*>[\s\S]*?<\/div>\s*$/i, '').trim();
      out = out.replace(/(?:<p[^>]*><br\s*\/?><\/p>\s*)*(?:<p[^>]*>--\s*<\/p>\s*)(?:<p[^>]*><br\s*\/?><\/p>\s*)*$/i, '').trim();
    }
    return out;
  };

  // Prefill when opened as "Edit as new": use state if present, else load message by id from ?editAsNew= (fallback).
  const editAsNewFromState = (location.state as { editAsNew?: Email } | null)?.editAsNew;
  const editAsNewEmail = editAsNewFromState ?? (editAsNewId && editAsNewFetchedEmail ? editAsNewFetchedEmail : null);
  const editAsNewAppliedRef = useRef(false);

  const aiThreadEmailId = useMemo((): number | undefined => {
    const candidates = [originalEmail?.id, composeEmailId ?? undefined, editAsNewEmail?.id];
    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
        return candidate;
      }
    }
    return undefined;
  }, [originalEmail?.id, composeEmailId, editAsNewEmail?.id]);

  const aiThreadPartitionAt =
    threadPartitionAt ?? originalEmail?.partitionAt ?? editAsNewEmail?.partitionAt ?? undefined;
  const aiThreadClientId = originalEmail?.linkedClient?.clientId ?? editAsNewEmail?.linkedClient?.clientId;
  const importedMessageAppliedRef = useRef(false);
  const hasLoadedDraft = useRef(false);
  const currentDraftIdRef = useRef<number | null>(null);
  const composeSessionIdRef = useRef(startNewComposeSession());
  const draftPrefillHandledRef = useRef<string | boolean>(false);
  const initialToHandledRef = useRef(false);
  const appliedTemplateIdRef = useRef<number | null>(null);
  const templateFromNavigation = (location.state as { template?: EmailTemplate } | null)?.template;
  useEffect(() => {
    if (!editAsNewEmail || id || editAsNewAppliedRef.current || !accounts.length) return;
    editAsNewAppliedRef.current = true;
    setBaselinePending(true);
    const formatAddr = (a: { name?: string; email: string }) => {
      const name = (a?.name ?? '').trim();
      const email = a?.email ?? '';
      return name && email ? `${name} <${email}>` : email;
    };
    const toList = Array.isArray(editAsNewEmail.to) ? editAsNewEmail.to : editAsNewEmail.to ? [editAsNewEmail.to] : [];
    const ccList = Array.isArray(editAsNewEmail.cc) ? editAsNewEmail.cc : editAsNewEmail.cc ? [editAsNewEmail.cc] : [];
    const bccList = Array.isArray(editAsNewEmail.bcc) ? editAsNewEmail.bcc : editAsNewEmail.bcc ? [editAsNewEmail.bcc] : [];
    const toStr = toList.map(formatAddr).filter(Boolean).join(', ');
    const ccStr = ccList.map(formatAddr).filter(Boolean).join(', ');
    const bccStr = bccList.map(formatAddr).filter(Boolean).join(', ');
    const accId = editAsNewEmail.emailAccountId || accounts[0]?.id || 0;
    const bodyHtml = editAsNewEmail.bodyHtml
      ? sanitizeEmailBodyForDisplay(editAsNewEmail.bodyHtml, editAsNewEmail.id, API_BASE_URL, editAsNewEmail.partitionAt)
      : (editAsNewEmail.bodyText
          ? `<pre style="margin:0;white-space:pre-wrap;word-break:break-word">${String(editAsNewEmail.bodyText).replace(/</g, '&lt;').replace(/&/g, '&amp;')}</pre>`
          : '');
    // Wrap in raw-html block so the editor renders it in an iframe and preserves styling and images (like forwarded content).
    const wrappedHtml = bodyHtml
      ? `<div data-slate-raw-html="true" data-html="${encodeURIComponent(bodyHtml)}"></div>`
      : '';
    setFormData({
      accountId: accId,
      sendIdentityId: undefined,
      to: toStr,
      cc: ccStr,
      bcc: bccStr,
      subject: editAsNewEmail.subject ?? '',
      html: wrappedHtml,
    });
    applyRecipientFields({ to: toStr, cc: ccStr, bcc: bccStr });
    setEditorContentKey((k) => k + 1);
  }, [editAsNewEmail, id, accounts, applyRecipientFields]);

  // Edit as new: pull non-inline attachments from the server into compose (same indices as GET .../attachments/:index).
  // Deps are id/partition only so SWR object identity churn does not wipe re-fetched files.
  useEffect(() => {
    if (!editAsNewEmail?.id || id) return;
    const meta = getNonEmbeddedAttachments(editAsNewEmail.attachments);
    setAttachments([]);
    if (meta.length === 0) {
      setBaselinePending(true);
      requestPrefillBaseline();
      return;
    }

    let cancelled = false;
    editAsNewAttachmentsPendingRef.current = true;
    const emailId = editAsNewEmail.id;

    void (async () => {
      setComposeLoading({
        message: `Loading attachments (0 of ${meta.length})…`,
        progress: 0,
      });
      try {
        const next = await loadNonEmbeddedEmailAttachments(
          emailId,
          editAsNewEmail.partitionAt,
          meta,
          'edit-as-new',
          (loaded, total) => {
            setComposeLoading({
              message: `Loading attachments (${loaded} of ${total})…`,
              progress: Math.round((loaded / total) * 100),
            });
          },
        );
        if (cancelled) return;
        if (next.length) {
          setAttachments(next);
          setAttachmentsExpanded(true);
        }
      } catch (err) {
        console.error('Edit as new: failed to load attachment', err);
        if (!cancelled) showError('Could not load one or more attachments.');
      } finally {
        if (!cancelled) {
          editAsNewAttachmentsPendingRef.current = false;
          setComposeLoading(null);
          requestPrefillBaseline();
        }
      }
    })();

    return () => {
      cancelled = true;
      editAsNewAttachmentsPendingRef.current = false;
      setComposeLoading(null);
    };
  }, [editAsNewEmail?.id, editAsNewEmail?.partitionAt, id, requestPrefillBaseline, showError]);

  useEffect(() => {
    if (!isReply || !originalEmail) return;
    const t = window.setTimeout(() => markComposeSaved(), 600);
    return () => window.clearTimeout(t);
  }, [originalEmail?.id, isReply, markComposeSaved]);

  useEffect(() => {
    if (savedSnapshot !== null) return;
    if (isReply || isForward || id) return;
    if (hasLoadedDraft.current) return;
    if (editAsNewFromState || editAsNewId) return;
    if (templateFromNavigation?.id) return;
    if (baselinePending) return;
    const t = window.setTimeout(() => markComposeSaved(), 400);
    return () => window.clearTimeout(t);
  }, [savedSnapshot, isReply, isForward, id, editAsNewFromState, editAsNewId, markComposeSaved, templateFromNavigation?.id, baselinePending]);

  // Initialize form data for reply/forward: user zone + signature + quoted panel (Thunderbird-style).
  const accIdReplyForward = originalEmail?.emailAccountId || accounts[0]?.id || 0;
  const sigForReplyForward = originalEmail && (isReply || isForward) ? getSignatureForContext(accIdReplyForward) : '';
  useEffect(() => {
    if (!originalEmail || (!isReply && !isForward)) return;
    if (!composeDefaultsReady) return;
    replyForwardSigInsertedRef.current = false;
    const accId = originalEmail.emailAccountId || accounts[0]?.id || 0;
    const introHtml = getReplyForwardIntroHtml();
    const mode = isForward ? 'forward' : 'reply';
    let doc = emailToComposeDoc(originalEmail, mode, {
      signature: sigForReplyForward ? wrapSignatureHtml(sigForReplyForward) : undefined,
    });
    const aiInitialBody =
      (location.state as { initialUserBody?: string } | null)?.initialUserBody?.trim() ||
      takeComposeInitialUserBody()?.trim() ||
      undefined;
    let userBodyHtml = stripLockedComposeBlocksFromUserBody(trimTrailingEmptyComposeParagraphs(introHtml));
    if (aiInitialBody) {
      if (aiInitialBody.includes('<')) {
        userBodyHtml = aiInitialBody;
      } else {
        const escaped = aiInitialBody
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>');
        userBodyHtml = `<p>${escaped}</p>`;
      }
    } else {
      userBodyHtml = applyComposeDefaultsToUserBody(userBodyHtml, composeDefaults);
    }
    // User typing zone always uses rich editor (toolbar); quoted original stays in panel below.
    doc = {
      ...doc,
      bodyKind: 'rich',
      userBody: userBodyHtml,
    };
    if (sigForReplyForward) replyForwardSigInsertedRef.current = true;

    if (isReply) {
      const from = Array.isArray(originalEmail.from) ? originalEmail.from[0] : originalEmail.from;
      const toAddr = (from?.email ?? (from && typeof from === 'object' && 'address' in from ? (from as { address?: string }).address : '')) || '';
      let ccVal = '';
      if (isReplyAll) {
        const myAccount = accounts.find((a) => a.id === accId);
        const myEmail = (myAccount?.emailAddress ?? '').toLowerCase();
        const toList = Array.isArray(originalEmail.to) ? originalEmail.to : originalEmail.to ? [originalEmail.to] : [];
        const ccList = Array.isArray(originalEmail.cc) ? originalEmail.cc : originalEmail.cc ? [originalEmail.cc] : [];
        const addrToEmail = (a: { name?: string; email: string } | string) =>
          (typeof a === 'string' ? a : a?.email ?? '') || '';
        const allRecipients = [...toList, ...ccList];
        const ccEmails = allRecipients
          .map(addrToEmail)
          .filter((e) => e && e.toLowerCase() !== myEmail);
        const seen = new Set<string>();
        const unique = ccEmails.filter((e) => {
          const key = e.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        ccVal = unique.map((e) => {
          const rec = allRecipients.find((r) => addrToEmail(r) === e);
          const name = rec && typeof rec === 'object' && 'name' in rec ? (rec as { name?: string }).name : '';
          return name?.trim() ? `${name} <${e}>` : e;
        }).join(', ');
      }
      applyComposeDoc(doc);
      const replyRecipients = {
        to: toAddr,
        cc: ccVal,
        bcc: '',
      };
      setFormData({
        accountId: accId,
        sendIdentityId: undefined,
        ...replyRecipients,
        subject: originalEmail.subject?.startsWith('Re:') ? originalEmail.subject : `Re: ${originalEmail.subject || '(no subject)'}`,
        html: composeDocToEditorHtml(doc),
      });
      applyRecipientFields(replyRecipients);
    } else {
      applyComposeDoc(doc);
      const forwardRecipients = { to: '', cc: '', bcc: '' };
      setFormData({
        accountId: accId,
        sendIdentityId: undefined,
        ...forwardRecipients,
        subject: originalEmail.subject?.startsWith('Fwd:') || originalEmail.subject?.startsWith('Fw:') ? originalEmail.subject : `Fwd: ${originalEmail.subject || '(no subject)'}`,
        html: composeDocToEditorHtml(doc),
      });
      applyRecipientFields(forwardRecipients);
    }
  }, [originalEmail, isReply, isReplyAll, isForward, accounts, applyComposeDoc, sigForReplyForward, applyRecipientFields, location.state, composeDefaults, composeDefaultsReady]);

  // Forward: pull non-inline attachments from the original message into compose.
  useEffect(() => {
    if (!isForward || !originalEmail?.id) return;
    const meta = getNonEmbeddedAttachments(originalEmail.attachments);
    setAttachments([]);
    if (meta.length === 0) {
      forwardAttachmentsPendingRef.current = false;
      const t = window.setTimeout(() => markComposeSaved(), 600);
      return () => window.clearTimeout(t);
    }

    let cancelled = false;
    forwardAttachmentsPendingRef.current = true;
    const emailId = originalEmail.id;

    void (async () => {
      setComposeLoading({
        message: `Loading attachments (0 of ${meta.length})…`,
        progress: 0,
      });
      try {
        const next = await loadNonEmbeddedEmailAttachments(
          emailId,
          originalEmail.partitionAt,
          meta,
          'forward',
          (loaded, total) => {
            setComposeLoading({
              message: `Loading attachments (${loaded} of ${total})…`,
              progress: Math.round((loaded / total) * 100),
            });
          },
        );
        if (cancelled) return;
        if (next.length) {
          setAttachments(next);
          setAttachmentsExpanded(true);
        }
        markComposeSaved(next.length > 0 ? { attachments: next } : undefined);
      } catch (err) {
        console.error('Forward: failed to load attachment', err);
        if (!cancelled) showError('Could not load one or more attachments from the original message.');
        if (!cancelled) markComposeSaved();
      } finally {
        if (!cancelled) {
          forwardAttachmentsPendingRef.current = false;
          setComposeLoading(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      forwardAttachmentsPendingRef.current = false;
      setComposeLoading(null);
    };
  }, [originalEmail?.id, originalEmail?.partitionAt, originalEmail?.attachments, isForward, markComposeSaved, showError]);

  // When signatures load after reply/forward opened, attach signature to compose doc.
  useEffect(() => {
    if (!originalEmail || (!isReply && !isForward) || !sigForReplyForward || replyForwardSigInsertedRef.current) return;
    if (composeDocRef.current.signature?.includes('email-signature')) {
      replyForwardSigInsertedRef.current = true;
      return;
    }
    replyForwardSigInsertedRef.current = true;
    applyComposeDoc({
      ...composeDocRef.current,
      signature: wrapSignatureHtml(sigForReplyForward),
    });
  }, [originalEmail, isReply, isForward, sigForReplyForward, applyComposeDoc]);

  // For new compose: seed default formatting and attach account signature (preview zone, not inside Slate).
  const accIdForNewCompose = formData.accountId || accounts[0]?.id || 0;
  const sigForNewCompose = getSignatureForContext(accIdForNewCompose);
  const appliedComposeDefaultsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (editAsNewFromState || editAsNewId || importedMessageAppliedRef.current) return;
    if (hasLoadedDraft.current || draftPrefillHandledRef.current) return;
    if (templateFromNavigation?.id || appliedTemplateIdRef.current != null) return;
    if (isReply || isForward || !accounts.length) return;
    if (!composeDefaultsReady) return;

    const defaultsKey = [
      composeDefaults.paragraphStyle,
      composeDefaults.fontFamily,
      composeDefaults.fontSize,
      composeDefaults.fontColor,
    ].join('\0');
    const stripped = stripSignature(composeDocRef.current.userBody);
    const bodyIsPlaceholder = isEmptyComposeUserBody(stripped);
    const alreadyApplied = appliedComposeDefaultsKeyRef.current === defaultsKey;
    const needsDefaultBody = bodyIsPlaceholder && !alreadyApplied;
    const needsSignature =
      Boolean(sigForNewCompose) && !composeDocRef.current.signature?.includes('email-signature');

    if (!needsDefaultBody && !needsSignature) return;
    // Only attach signature onto an empty/default typing area (same as before).
    if (needsSignature && !needsDefaultBody && !bodyIsPlaceholder) return;

    const defaultBody = buildDefaultComposeUserBodyHtml(composeDefaults);
    const accId = formData.accountId || accounts[0]?.id || 0;
    let doc: ComposeBodyDocument = {
      ...composeDocRef.current,
      bodyKind: 'rich',
      userBody: needsDefaultBody ? defaultBody : composeDocRef.current.userBody,
    };
    if (needsSignature && sigForNewCompose) {
      doc = attachAccountSignature(doc, sigForNewCompose);
    }
    if (needsDefaultBody) {
      appliedComposeDefaultsKeyRef.current = defaultsKey;
    }
    applyComposeDoc(doc);
    setFormData((prev) => (prev.accountId === accId ? prev : { ...prev, accountId: accId }));
  }, [
    editAsNewFromState,
    editAsNewId,
    templateFromNavigation?.id,
    isReply,
    isForward,
    accounts.length,
    formData.accountId,
    sigForNewCompose,
    applyComposeDoc,
    composeDefaults,
    composeDefaultsReady,
  ]);

  /** Read the latest editor body before persisting drafts/templates. */
  const resolveComposeDocForPersistence = useCallback((): ComposeBodyDocument => {
    const doc = composeDocRef.current;
    return buildComposeDocForPersistence({
      doc,
      liveEditorHtml: editorRef.current?.getHtml?.(),
    });
  }, []);

  const getTemplateBodyForSave = useCallback(() => {
    const liveEditorHtml = editorRef.current?.getHtml?.();
    const docForSave = buildComposeDocForTemplatePersistence({
      doc: composeDocRef.current,
      liveEditorHtml,
    });
    const outbound = composeDocToOutbound(docForSave, { includeSignature: true, includeQuoted: true });
    return {
      html: outbound.html || undefined,
      text: outbound.text || undefined,
      bodyKind: docForSave.bodyKind,
      composeState: docForSave,
    };
  }, []);

  // When account or signatures change, update compose doc signature (strip any legacy embedded signature from userBody).
  const signatureForAccount = getSignatureForContext(formData.accountId);
  useEffect(() => {
    if (appliedTemplateIdRef.current != null) return;
    if (isReply || isForward) return;
    if (!formData.accountId) return;
    if (hasLoadedDraft.current) return;
    if (skipNextSignatureSyncRef.current) {
      skipNextSignatureSyncRef.current = false;
      return;
    }

    setComposeDoc((prev) => {
      const strippedBody = stripSignature(prev.userBody);
      if (editAsNewAppliedRef.current) {
        if (strippedBody === prev.userBody && !prev.signature) return prev;
        const next = { ...prev, userBody: strippedBody, signature: undefined };
        setFormData((fd) => ({ ...fd, html: composeDocToEditorHtml(next) }));
        setEditorContentKey((k) => k + 1);
        return next;
      }
      const next = attachAccountSignature(
        { ...prev, userBody: strippedBody, bodyKind: 'rich' },
        signatureForAccount,
      );
      if (next.userBody === prev.userBody && next.signature === prev.signature) return prev;
      setFormData((fd) => ({ ...fd, html: composeDocToEditorHtml(next) }));
      setEditorContentKey((k) => k + 1);
      return next;
    });
  }, [formData.accountId, signatureForAccount, isReply, isForward]);

  // When send identities load for the current account, set default identity if none selected
  useEffect(() => {
    const idents = identitiesByAccount[formData.accountId]?.identities ?? [];
    if (idents.length > 0 && formData.sendIdentityId == null) {
      const defaultId = idents.find((i) => i.isDefault) ?? idents[0];
      if (defaultId) setFormData((prev) => ({ ...prev, sendIdentityId: defaultId.id }));
    }
  }, [formData.accountId, formData.sendIdentityId, identitiesByAccount]);

  // Convert compose doc to plain text for word count, hasContent, submit
  const outboundBody = useMemo(() => composeDocToOutbound(composeDoc), [composeDoc]);
  const plainText = outboundBody.text;

  const hasContent = hasComposeContentFromDoc(formData, composeDoc, plainText, stripSignature);

  const canAutoSaveDraft = !isReply && !isForward && Boolean(formData.accountId) && hasContent;

  const draftAutosaveIntervalSec = useMemo(() => {
    const raw = applicationSettings?.emailDraftAutosaveSeconds;
    if (raw == null || !Number.isFinite(raw)) return 30;
    return Math.min(600, Math.max(0, Math.trunc(raw)));
  }, [applicationSettings?.emailDraftAutosaveSeconds]);

  const periodicDraftAutosaveEnabled = canAutoSaveDraft && draftAutosaveIntervalSec > 0;

  const fromIdentityOptions = useMemo(() => {
    return Object.entries(identitiesByAccount).flatMap(([accountIdStr, acc]) => {
      const accountId = parseInt(accountIdStr, 10);
      const accountEmail = acc.emailAddress;
      return (acc.identities ?? []).map((identity) => ({
        identityId: identity.id,
        accountId,
        isDefault: identity.isDefault,
        label: `${identity.name} (${identity.emailAddress || accountEmail})`,
      }));
    });
  }, [identitiesByAccount]);

  const currentSnapshot = useMemo(
    () => makeComposeSnapshot(formData, attachments),
    [formData, attachments],
  );
  const hasUnsavedChanges = useMemo(() => {
    if (baselinePending) return false;
    if (savedSnapshot === null) {
      return hasContent || attachments.length > 0;
    }
    return !composeSnapshotsEqual(currentSnapshot, savedSnapshot);
  }, [currentSnapshot, savedSnapshot, hasContent, attachments.length, baselinePending]);

  useEffect(() => {
    if (fromIdentityOptions.length === 0) return;
    if (formData.sendIdentityId != null) return;
    const currentAccountDefault = fromIdentityOptions.find(
      (o) => o.accountId === formData.accountId && o.isDefault,
    );
    const globalDefault = fromIdentityOptions.find((o) => o.isDefault);
    const selected = currentAccountDefault ?? globalDefault ?? fromIdentityOptions[0];
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        accountId: selected.accountId,
        sendIdentityId: selected.identityId,
      }));
    }
  }, [fromIdentityOptions, formData.accountId, formData.sendIdentityId]);

  // Establish saved baseline after library template / draft / import prefill settles (form + attachments + identity).
  useEffect(() => {
    if (isReply || isForward || id) return;
    if (!baselinePending) return;
    if (appliedTemplateIdRef.current != null && templateEnrichPendingRef.current) return;
    if (editAsNewAttachmentsPendingRef.current) return;
    if (
      appliedTemplateIdRef.current != null &&
      fromIdentityOptions.length > 0 &&
      formData.sendIdentityId == null
    ) {
      return;
    }
    markComposeSaved();
  }, [
    baselinePending,
    prefillBaselineTick,
    formData,
    attachments,
    fromIdentityOptions.length,
    formData.sendIdentityId,
    isReply,
    isForward,
    id,
    markComposeSaved,
  ]);

  /** Set right before an intentional exit (send / save draft / discard) so a standalone tab can window.close() without a "Leave site?" prompt. */
  const allowUnloadRef = useRef(false);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allowUnloadRef.current) return;
      if (hasUnsavedChanges && !isSending) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges, isSending]);

  const draftSaveInFlightRef = useRef(false);

  const resolveActiveDraftId = useCallback((): number | null => {
    if (currentDraftIdRef.current != null) return currentDraftIdRef.current;
    return readComposeSessionDraftId(composeSessionIdRef.current);
  }, []);

  const bindActiveDraftId = useCallback((draftId: number) => {
    currentDraftIdRef.current = draftId;
    writeComposeSessionDraftId(composeSessionIdRef.current, draftId);
  }, []);

  const resetComposeDraftSession = useCallback(() => {
    clearComposeSessionDraftId(composeSessionIdRef.current);
    currentDraftIdRef.current = null;
    composeSessionIdRef.current = startNewComposeSession();
    appliedComposeDefaultsKeyRef.current = null;
  }, []);

  const persistComposeDraft = useCallback(async (): Promise<void> => {
    try {
      const docForSave = resolveComposeDocForPersistence();
      const outbound = composeDocToOutbound(docForSave, {
        includeSignature: true,
        includeQuoted: true,
      });
      const recipients = resolveRecipientsForPersistence();
      const existingDraftId = resolveActiveDraftId();
      const saved = await saveDraftApi({
        draftId: existingDraftId ?? undefined,
        accountId: formData.accountId,
        ...recipients,
        subject: formData.subject || undefined,
        html: outbound.html || undefined,
        text: outbound.text || undefined,
        bodyKind: docForSave.bodyKind,
        composeState: docForSave,
      });
      bindActiveDraftId(saved.id);
      await revalidateEmailDraftCaches(saved.id);
    } catch (err: unknown) {
      console.error('Failed to save draft', err);
      throw err;
    }
  }, [
    bindActiveDraftId,
    formData.accountId,
    formData.subject,
    resolveActiveDraftId,
    resolveComposeDocForPersistence,
    resolveRecipientsForPersistence,
  ]);

  const saveDraftToStorage = useCallback(async (): Promise<boolean> => {
    if (!canAutoSaveDraft || draftSaveInFlightRef.current) return false;
    draftSaveInFlightRef.current = true;
    try {
      return await enqueueComposeDraftSave(persistComposeDraft);
    } finally {
      draftSaveInFlightRef.current = false;
    }
  }, [canAutoSaveDraft, persistComposeDraft]);

  const { secondsUntilSave, status: draftAutosaveStatus, resetCountdown } = useComposeDraftAutosave({
    enabled: periodicDraftAutosaveEnabled,
    intervalSec: draftAutosaveIntervalSec,
    onAutosave: saveDraftToStorage,
  });

  const clearPersistedDraft = useCallback(async () => {
    const draftId = resolveActiveDraftId();
    if (!draftId) return;
    try {
      await deleteDraftApi(draftId);
      resetComposeDraftSession();
      void revalidateEmailDraftCaches(draftId);
    } catch {
      // Draft may already be gone.
    }
  }, [resolveActiveDraftId, resetComposeDraftSession]);

  /** Keep draft for reuse: persist latest content, then unbind session so the next compose is a new draft. */
  const keepPersistedDraftAfterSend = useCallback(async () => {
    if (formData.accountId && hasContent) {
      try {
        await enqueueComposeDraftSave(persistComposeDraft);
      } catch {
        // Best-effort; draft may already exist from autosave.
      }
    }
    resetComposeDraftSession();
  }, [formData.accountId, hasContent, persistComposeDraft, resetComposeDraftSession]);

  useEffect(() => {
    if (!canAutoSaveDraft) return;
    const onBlur = () => {
      void saveDraftToStorage().then((saved) => {
        if (saved) resetCountdown();
      });
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [canAutoSaveDraft, saveDraftToStorage, resetCountdown]);

  const applyLibraryTemplateToForm = useCallback(
    (full: EmailTemplate) => {
      appliedTemplateIdRef.current = full.id;
      currentDraftIdRef.current = null;
      composeSessionIdRef.current = startNewComposeSession();
      hasLoadedDraft.current = true;
      setOpenedLibraryTemplate(full);
      setBaselinePending(true);
      skipNextSignatureSyncRef.current = true;
      ignoreEmptyEditorSyncUntilRef.current = Date.now() + 1500;
      const data = templateToFormData(full);
      let doc = templateToComposeDoc(full);
      const accId =
        data.accountId && accounts.some((a) => a.id === data.accountId)
          ? data.accountId
          : accounts[0]?.id ?? 0;
      const sig = getSignatureForContext(accId);
      if (sig && !doc.signature) {
        doc = attachAccountSignature(doc, sig);
      }
      applyComposeDoc(doc);
      const idents = identitiesByAccount[accId]?.identities ?? [];
      const defaultIdentity = idents.find((i) => i.isDefault) ?? idents[0];
      setFormData((prev) => ({
        ...prev,
        ...data,
        html: composeDocToEditorHtml(doc),
        accountId: accId || prev.accountId,
        sendIdentityId: defaultIdentity?.id ?? prev.sendIdentityId,
      }));
      applyRecipientFields({
        to: data.to,
        cc: data.cc,
        bcc: data.bcc,
      });
    },
    [accounts, getSignatureForContext, identitiesByAccount, applyComposeDoc, applyRecipientFields],
  );

  const enrichAndLoadLibraryTemplate = useCallback(
    async (template: EmailTemplate, options?: { isCancelled?: () => boolean }) => {
      const loadGen = ++templateLoadGenRef.current;
      const isStale = () =>
        loadGen !== templateLoadGenRef.current || Boolean(options?.isCancelled?.());

      setAttachments([]);
      appliedTemplateIdRef.current = template.id;
      templateEnrichPendingRef.current = true;
      setComposeLoading({ message: 'Loading template…' });
      try {
        const full = await getTemplateApi(template.id);
        if (isStale()) return;
        applyLibraryTemplateToForm(full);
        if (full.attachments?.length) {
          const total = full.attachments.length;
          const files: File[] = [];
          for (let i = 0; i < total; i++) {
            setComposeLoading({
              message: `Loading template attachments (${i + 1} of ${total})…`,
              progress: Math.round(((i + 1) / total) * 100),
            });
            files.push(await fetchTemplateAttachmentFile(full.id, full.attachments[i]));
            if (isStale()) return;
          }
          setAttachments(
            files.map((f) => ({
              id: `tpl-${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 9)}`,
              file: f,
              name: f.name,
              size: f.size,
            })),
          );
          setAttachmentsExpanded(true);
        }
      } catch (err) {
        console.error('Failed to load template', err);
        if (isStale()) return;
        showError('Could not load full template; using saved preview only.');
        applyLibraryTemplateToForm(template);
      } finally {
        if (loadGen === templateLoadGenRef.current) {
          templateEnrichPendingRef.current = false;
          setComposeLoading(null);
          if (!options?.isCancelled?.()) {
            requestPrefillBaseline();
          }
        }
      }
    },
    [applyLibraryTemplateToForm, requestPrefillBaseline, showError],
  );

  const enrichAndLoadLibraryTemplateRef = useRef(enrichAndLoadLibraryTemplate);
  enrichAndLoadLibraryTemplateRef.current = enrichAndLoadLibraryTemplate;

  const handlePickLibraryTemplate = useCallback(
    (template: EmailTemplate) => {
      if (hasUnsavedChanges) {
        setLibraryReplaceConfirm(template);
        return;
      }
      void enrichAndLoadLibraryTemplate(template);
    },
    [hasUnsavedChanges, enrichAndLoadLibraryTemplate],
  );

  const loadDraftIntoCompose = useCallback(
    (draft: EmailDraft | EmailDraftListItem, options?: { asCopy?: boolean }) => {
      const asCopy = options?.asCopy === true || draft.isOwner === false;
      hasLoadedDraft.current = true;
      if (asCopy) {
        // Content only — autosave will create a new draft owned by the current user.
        clearComposeSessionDraftId(composeSessionIdRef.current);
        currentDraftIdRef.current = null;
        composeSessionIdRef.current = startNewComposeSession();
      } else {
        bindActiveDraftId(draft.id);
      }
      draftPrefillHandledRef.current =
        draft.id != null
          ? `id:${draft.id}${asCopy ? ':copy' : ''}`
          : draft.emailAccountId != null
            ? `account:${draft.emailAccountId}`
            : true;
      setBaselinePending(true);
      const data = draftToFormData(draft);
      const accId =
        data.accountId && accounts.some((a) => a.id === data.accountId)
          ? data.accountId
          : accounts[0]?.id ?? 0;
      const baseDoc = composeDocFromDraft(draft);
      const doc = attachAccountSignature(baseDoc, getSignatureForContext(accId));
      skipNextSignatureSyncRef.current = true;
      applyComposeDoc(doc);
      setFormData((prev) => ({
        ...prev,
        ...data,
        html: composeDocToEditorHtml(doc),
        accountId: accId || prev.accountId,
        sendIdentityId: prev.sendIdentityId,
      }));
      applyRecipientFields({
        to: data.to,
        cc: data.cc,
        bcc: data.bcc,
      });
      requestPrefillBaseline();
    },
    [accounts, applyComposeDoc, getSignatureForContext, requestPrefillBaseline, applyRecipientFields, bindActiveDraftId],
  );

  const loadOutboxIntoCompose = useCallback(
    (detail: EmailOutboxDetail) => {
      hasLoadedDraft.current = true;
      draftPrefillHandledRef.current = `outbox:${detail.id}`;
      setBaselinePending(true);
      const payload = detail.payload;
      const accId =
        detail.emailAccountId && accounts.some((a) => a.id === detail.emailAccountId)
          ? detail.emailAccountId
          : accounts[0]?.id ?? 0;
      const html = payload?.html ?? '';
      const text = payload?.text ?? '';
      const userBody = importMessageToEditableEditorHtml(html || null, text || null) || '<p><br></p>';
      const doc = attachAccountSignature(
        { bodyKind: 'rich', userBody, quoted: undefined, signature: undefined },
        getSignatureForContext(accId),
      );
      skipNextSignatureSyncRef.current = true;
      applyComposeDoc(doc);
      const to = (payload?.to ?? detail.recipients?.to ?? []).join(', ');
      const cc = (payload?.cc ?? detail.recipients?.cc ?? []).join(', ');
      const bcc = (payload?.bcc ?? detail.recipients?.bcc ?? []).join(', ');
      setFormData((prev) => ({
        ...prev,
        accountId: accId || prev.accountId,
        sendIdentityId: payload?.sendIdentityId ?? prev.sendIdentityId,
        subject: payload?.subject ?? detail.subject ?? '',
        to,
        cc,
        bcc,
        html: composeDocToEditorHtml(doc),
      }));
      applyRecipientFields({ to, cc, bcc });
      if (cc.trim()) setShowCc(true);
      if (bcc.trim()) setShowBcc(true);
      requestPrefillBaseline();
    },
    [accounts, applyComposeDoc, getSignatureForContext, requestPrefillBaseline, applyRecipientFields],
  );

  // When opening compose, start a fresh draft session unless resuming a saved draft.
  useEffect(() => {
    if (isReply || isForward || id) return;
    const state = location.state as {
      draftId?: number;
      draft?: EmailDraft | EmailDraftListItem;
      draftAsCopy?: boolean;
      template?: EmailTemplate;
      outboxId?: number;
    } | null;
    const resumeDraftId = state?.draftId ?? state?.draft?.id;
    const asCopy = state?.draftAsCopy === true || state?.draft?.isOwner === false;
    if (resumeDraftId != null && !asCopy) {
      bindActiveDraftId(resumeDraftId);
      return;
    }
    if (state?.template?.id || state?.outboxId != null) {
      return;
    }
    // Fresh compose, or opening someone else's draft as a copy (do not bind their draft id).
    resetComposeDraftSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when navigation key changes
  }, [location.key, isReply, isForward, id, bindActiveDraftId, resetComposeDraftSession]);

  // When opening new compose (Add), optionally load template or initial recipient.
  // Auto-restore of saved drafts is disabled so a fresh "New message" stays empty.
  useEffect(() => {
    if (isReply || isForward || id) return;
    if (editAsNewFromState || editAsNewId) return;
    if (importedMessageAppliedRef.current) return;

    const composeState = location.state as {
      draft?: EmailDraft | EmailDraftListItem;
      draftId?: number;
      draftAccountId?: number;
      draftAsCopy?: boolean;
      template?: EmailTemplate;
      initialTo?: string;
      outboxId?: number;
    } | null;

    const outboxId = composeState?.outboxId;
    if (outboxId != null && draftPrefillHandledRef.current !== `outbox:${outboxId}`) {
      let cancelled = false;
      setComposeLoading({ message: 'Loading message…' });
      void (async () => {
        try {
          const detail = await fetchOutboxApi(outboxId);
          if (cancelled) return;
          loadOutboxIntoCompose(detail);
        } catch {
          if (!cancelled) showError('Failed to load outbox message');
        } finally {
          if (!cancelled) setComposeLoading(null);
        }
      })();
      return () => {
        cancelled = true;
        setComposeLoading(null);
      };
    }

    const draftFromState = composeState?.draft;
    const draftAccountId = composeState?.draftAccountId ?? draftFromState?.emailAccountId;
    const draftId = composeState?.draftId ?? draftFromState?.id;
    const draftAsCopy =
      composeState?.draftAsCopy === true || draftFromState?.isOwner === false;
    const draftLoadKey =
      draftId != null
        ? `id:${draftId}${draftAsCopy ? ':copy' : ''}`
        : draftAccountId != null
          ? `account:${draftAccountId}`
          : null;

    if (draftLoadKey && draftPrefillHandledRef.current !== draftLoadKey) {
      let cancelled = false;
      setComposeLoading({ message: 'Loading draft…' });
      void (async () => {
        try {
          let draft: EmailDraft | EmailDraftListItem | null | undefined = draftFromState;
          if (draftId != null) {
            try {
              const fresh = await fetchDraftApi(draftId);
              if (fresh) draft = fresh;
            } catch {
              // fall back to navigation state
            }
          }
          if (cancelled || !draft) return;
          const asCopy = draftAsCopy || draft.isOwner === false;
          loadDraftIntoCompose(draft, { asCopy });
        } finally {
          if (!cancelled) setComposeLoading(null);
        }
      })();
      return () => {
        cancelled = true;
        setComposeLoading(null);
      };
    }

    // Load initial "Compose To" from state (when starting from address menu)
    const initialTo = composeState?.initialTo;
    if (initialTo && !initialToHandledRef.current) {
      initialToHandledRef.current = true;
      hasLoadedDraft.current = true;
      setBaselinePending(true);
      setFormData((prev) => ({
        ...prev,
        to: initialTo,
      }));
      applyRecipientFields({ to: initialTo });
      setEditorContentKey((k) => k + 1);
      requestPrefillBaseline();
    }
  }, [isReply, isForward, id, editAsNewFromState, editAsNewId, location.state?.draftId, location.state?.draftAccountId, location.state?.initialTo, location.state?.outboxId, requestPrefillBaseline, loadDraftIntoCompose, loadOutboxIntoCompose, showError, applyRecipientFields]);

  // Library template from navigation — isolated so accounts/callback churn cannot cancel the load.
  useEffect(() => {
    if (isReply || isForward || id) return;
    if (editAsNewFromState || editAsNewId) return;
    if (importedMessageAppliedRef.current) return;

    const templateFromState = (location.state as { template?: EmailTemplate } | null)?.template;
    if (!templateFromState?.id) return;
    if (appliedTemplateIdRef.current === templateFromState.id && !templateEnrichPendingRef.current) {
      return;
    }

    let cancelled = false;
    void enrichAndLoadLibraryTemplateRef.current(templateFromState, {
      isCancelled: () => cancelled,
    });
    return () => {
      cancelled = true;
    };
  }, [isReply, isForward, id, editAsNewFromState, editAsNewId, location.state?.template?.id]);

  const confirmDiscard = useCallback(() => {
    if (!hasUnsavedChanges) {
      allowUnloadRef.current = true;
      exitCompose(navigate, location);
      return;
    }
    setShowDiscardConfirm(true);
  }, [hasUnsavedChanges, navigate, location]);

  const doDiscard = useCallback(() => {
    try {
      localStorage.removeItem(COMPOSE_DRAFT_KEY);
    } catch {
      // ignore
    }
    if (!isReply && !isForward) {
      void clearPersistedDraft();
    }
    setShowDiscardConfirm(false);
    allowUnloadRef.current = true;
    exitCompose(navigate, location);
  }, [navigate, location, isReply, isForward, clearPersistedDraft]);

  const activeComposeLoading = useMemo((): ComposeLoadingState | null => {
    if (composeLoading) return composeLoading;
    if ((isReply || isForward) && originalEmailLoading && !originalEmail) {
      return {
        message: isForward ? 'Loading message to forward…' : 'Loading message to reply…',
      };
    }
    if (isForward && forwardAttachmentsPendingRef.current) {
      return composeLoading ?? { message: 'Loading attachments…' };
    }
    if (editAsNewId && !editAsNewFromState && editAsNewEmailLoading && !editAsNewFetchedEmail) {
      return { message: 'Loading message…' };
    }
    if (isImportingMessage) {
      return { message: 'Importing message…' };
    }
    return null;
  }, [
    composeLoading,
    isReply,
    isForward,
    originalEmailLoading,
    originalEmail,
    editAsNewId,
    editAsNewFromState,
    editAsNewEmailLoading,
    editAsNewFetchedEmail,
    isImportingMessage,
  ]);

  const isComposeBusy = activeComposeLoading != null;

  useEffect(() => {
    if (isComposeBusy || hasFocusedComposeRef.current) return;
    const t = setTimeout(() => {
      subjectInputRef.current?.focus();
      hasFocusedComposeRef.current = true;
    }, 100);
    return () => clearTimeout(t);
  }, [isComposeBusy]);
  const totalAttachmentSize = attachments.reduce((s, a) => s + a.size, 0);
  const attachmentMaxSizeMb = Math.max(
    1,
    Math.min(
      100,
      applicationSettings?.emailAttachmentMaxSizeMb ??
        APP_CONFIG.api.emailAttachmentMaxSizeMb ??
        DEFAULT_ATTACHMENT_SIZE_LIMIT_MB,
    ),
  );
  const attachmentSizeLimitBytes = attachmentMaxSizeMb * 1024 * 1024;
  const attachmentsOverLimit = totalAttachmentSize > attachmentSizeLimitBytes;

  const addAttachmentFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    setAttachments((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({ id: `${Date.now()}-${file.name}`, file, name: file.name, size: file.size })),
    ]);
    setAttachmentsExpanded(true);
  }, []);

  const handleAttachFromLibrary = useCallback(
    async (docs: EmailTemplateDocument[]) => {
      const files = await Promise.all(docs.map((doc) => templateDocumentToFile(doc)));
      addAttachmentFiles(files);
      showSuccess(
        `${files.length} document${files.length === 1 ? '' : 's'} attached from library`,
      );
    },
    [addAttachmentFiles, showSuccess],
  );

  const handleImportMessageFile = useCallback(
    async (file: File) => {
      if (!isComposeMessageFile(file)) {
        showError('Please choose a .eml or .msg file.');
        return;
      }
      setIsImportingMessage(true);
      setComposeLoading({ message: 'Importing message…' });
      setOpenedLibraryTemplate(null);
      try {
        const parsed = await importComposeMessage(file);
        const to = formatAddrList(parsed.to);
        const cc = formatAddrList(parsed.cc);
        const bcc = formatAddrList(parsed.bcc);
        importedMessageAppliedRef.current = true;
        hasLoadedDraft.current = true;
        draftPrefillHandledRef.current = 'imported';
        setBaselinePending(true);
        let doc = importToComposeDoc(parsed);
        const accId = formData.accountId || accounts[0]?.id || 0;
        const sig = getSignatureForContext(accId);
        if (sig) {
          doc = { ...doc, signature: wrapSignatureHtml(sig) };
        }
        skipNextSignatureSyncRef.current = true;
        applyComposeDoc(doc);
        setFormData((prev) => ({
          ...prev,
          to,
          cc,
          bcc,
          subject: parsed.subject ?? '',
          html: composeDocToEditorHtml(doc),
        }));
        applyRecipientFields({ to, cc, bcc });
        if (cc.trim()) setShowCc(true);
        if (bcc.trim()) setShowBcc(true);
        setEditorContentKey((k) => k + 1);

        if (parsed.attachments?.length) {
          const files = parsed.attachments.map((att) =>
            base64ToFile(att.filename, att.contentType, att.content)
          );
          setAttachments(
            files.map((f) => ({
              id: `import-${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 9)}`,
              file: f,
              name: f.name,
              size: f.size,
            }))
          );
          setAttachmentsExpanded(true);
        } else {
          setAttachments([]);
        }
        requestPrefillBaseline();

        showSuccess('Message loaded from file.');
      } catch (err) {
        console.error('Failed to import message file', err);
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message
            : undefined;
        const detail = Array.isArray(message) ? message.join(', ') : message;
        showError(detail || 'Could not load message from file.');
        console.error('Compose import failed; API base:', API_BASE_URL, err);
      } finally {
        setIsImportingMessage(false);
        setComposeLoading(null);
      }
    },
    [
      showError,
      showSuccess,
      formData.accountId,
      accounts,
      getSignatureForContext,
      requestPrefillBaseline,
      applyComposeDoc,
      applyRecipientFields,
    ],
  );

  const openMessageFilePicker = useCallback(() => {
    messageFileInputRef.current?.click();
  }, []);

  const openComposeAttachment = useCallback(
    (file: File) => {
      try {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank', 'noopener');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        console.error('Failed to open attachment', e);
        showError('Could not open attachment.');
      }
    },
    [showError]
  );

  const handleAttachmentDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAttachmentDragOver(false);
    const files = e.dataTransfer.files;
    if (files?.length) addAttachmentFiles(Array.from(files));
  }, [addAttachmentFiles]);

  const handleAttachmentDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setAttachmentDragOver(true);
  }, []);

  const handleAttachmentDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setAttachmentDragOver(false);
  }, []);
  const subjectLength = formData.subject.length;
  const subjectOverLimit = subjectLength > SUBJECT_MAX_LENGTH;
  const toInvalid =
    touchedTo &&
    (!formData.to.trim() || getInvalidRecipientTokens(formData.to).length > 0);
  const subjectInvalid = touchedSubject && !formData.subject.trim();

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { startY: e.clientY, startHeight: editorHeight };
    const onMove = (ev: MouseEvent) => {
      const dy = ev.clientY - resizeRef.current.startY;
      const next = Math.min(editorHeightMax, Math.max(editorHeightMin, resizeRef.current.startHeight + dy));
      setEditorHeight(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [editorHeight, editorHeightMin, editorHeightMax]);

  const wordCount = useMemo(() => {
    const t = plainText.trim();
    return t ? t.split(/\s+/).length : 0;
  }, [plainText]);

  const formRef = useRef<HTMLFormElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const hasFocusedComposeRef = useRef(false);

  const canScheduleSend = !isReply && !isForward;

  /** Browser tab label: short mode prefix + subject (e.g. RE: … / FWD: … / New: …). */
  const documentTitle = useMemo(() => {
    const modeAbbrev = isReply ? 'RE' : isForward ? 'FWD' : 'New';
    const subject = formData.subject?.trim();
    if (!subject) return modeAbbrev;
    const maxLen = 48;
    const short = subject.length > maxLen ? `${subject.slice(0, maxLen - 1)}…` : subject;
    // Subject often already starts with Re:/Fwd: — avoid "RE: Re: …"
    const alreadyPrefixed = /^(re|fwd?|fw)\s*:/i.test(short);
    return alreadyPrefixed ? short : `${modeAbbrev}: ${short}`;
  }, [isReply, isForward, formData.subject]);
  useDocumentTitle(documentTitle);

  const toggleAiPanel = useCallback(() => {
    if (!enableAiEmails) return;
    setAiPanelOpen((v) => {
      const next = !v;
      if (next) {
        setAddressBookOpen(false);
        setSignaturesPanelOpen(false);
      }
      return next;
    });
  }, [enableAiEmails]);

  const captureAiSelection = useCallback(() => {
    aiSelectionCaptureRef.current = editorRef.current?.getSelectedText?.() ?? '';
  }, []);

  const signatureMenuOptions = useMemo(() => {
    const sigs = signaturesByAccount[formData.accountId];
    return (sigs?.signatures ?? [])
      .filter((sig) => sig.body)
      .map((sig) => ({
        label: sig.isDefault ? `${sig.name} (Default)` : sig.name,
        html: sig.body!,
      }));
  }, [signaturesByAccount, formData.accountId]);

  const handleInsertSignature = useCallback(
    (html: string) => {
      setComposeDoc((prev) => {
        const next = attachAccountSignature({ ...prev, userBody: stripSignature(prev.userBody) }, html);
        setFormData((fd) => ({ ...fd, html: composeDocToEditorHtml(next) }));
        return next;
      });
      setEditorContentKey((k) => k + 1);
    },
    [],
  );

  const validateBeforeSend = (): boolean => {
    setTouchedTo(true);
    setTouchedSubject(true);
    if (!outboundBody.text.trim() && !composeDoc.userBody.trim()) {
      showError('Please enter a message.');
      return false;
    }
    if (!isReply) {
      const toForValidation = parseRecipientFieldForSend(formData.to) ?? [];
      if (toForValidation.length === 0) {
        showError('Please enter at least one recipient.');
        return false;
      }
    }
    const invalidRecipients = [
      ...getInvalidRecipientTokens(formData.to).map((t) => `To: ${t}`),
      ...getInvalidRecipientTokens(formData.cc).map((t) => `Cc: ${t}`),
      ...getInvalidRecipientTokens(formData.bcc).map((t) => `Bcc: ${t}`),
    ];
    if (invalidRecipients.length > 0) {
      showError(
        `Please fix invalid email address${invalidRecipients.length > 1 ? 'es' : ''}: ${invalidRecipients.join(', ')}`,
      );
      return false;
    }
    if (attachmentsOverLimit) {
      showError(`Attachments exceed ${attachmentMaxSizeMb} MB. Remove some files.`);
      return false;
    }
    if (subjectOverLimit) {
      showError(`Subject must be ${SUBJECT_MAX_LENGTH} characters or fewer.`);
      return false;
    }
    return true;
  };

  const cancelSend = useCallback(() => {
    sendAbortRef.current?.abort();
  }, []);

  const performSend = async (scheduledSendAt?: string) => {
    sendAbortRef.current?.abort();
    const abortController = new AbortController();
    sendAbortRef.current = abortController;
    const { signal } = abortController;

    setIsSending(true);
    setSendError(null);
    const willHaveAttachments = attachments.length > 0;
    const sendStartedAt = Date.now();
    const elapsed = () => Date.now() - sendStartedAt;
    setSendHasAttachments(willHaveAttachments);
    setSendProgress({
      phase: willHaveAttachments ? 'preparing' : 'uploading',
      percent: 0,
      fileCount: willHaveAttachments ? attachments.length : undefined,
      tip: willHaveAttachments
        ? (applicationSettings?.emailAttachmentTransferMode ??
            APP_CONFIG.api.emailAttachmentTransferMode) === 'multipart'
          ? 'Preparing multipart attachment upload.'
          : 'Encoding attachments locally before upload.'
        : 'Sending your message to the server.',
      elapsedMs: 0,
    });

    try {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const toArray = parseRecipientFieldForSend(formData.to) ?? [];
      const ccArray = parseRecipientFieldForSend(formData.cc);
      const bccArray = parseRecipientFieldForSend(formData.bcc);

      if (!isReply && !isForward && toArray.length === 0) {
        showError('Please enter at least one recipient.');
        return;
      }

      let attachmentsPayload: SendAttachmentPayload[] | undefined;
      const useMultipart =
        attachments.length > 0 &&
        (applicationSettings?.emailAttachmentTransferMode ??
          APP_CONFIG.api.emailAttachmentTransferMode) === 'multipart';

      if (attachments.length > 0) {
        const totalBytes = attachments.reduce((sum, a) => sum + (a.file.size || 0), 0) || 1;

        if (useMultipart) {
          setSendProgress({
            phase: 'uploading',
            percent: 0,
            detail: 'Starting file upload…',
            fileCount: attachments.length,
            bytesLoaded: 0,
            bytesTotal: totalBytes,
            elapsedMs: elapsed(),
            tip: 'Uploading attachments as multipart (binary) before sending the message.',
          });

          const formDataUpload = new FormData();
          for (const a of attachments) {
            formDataUpload.append('files', a.file, a.name);
          }

          let uploadStartedAt: number | null = null;
          const stageRes = await api.post<{
            batchId: string;
            attachments: Array<{
              filename: string;
              path: string;
              contentType?: string;
              size: number;
            }>;
          }>('/emails/send-attachments', formDataUpload, {
            signal,
            timeout:
              applicationSettings?.emailSendWithAttachmentsTimeout ??
              APP_CONFIG.api.emailSendWithAttachmentsTimeout,
            onUploadProgress: (event: { loaded: number; total?: number }) => {
              if (uploadStartedAt == null) uploadStartedAt = Date.now();
              const uploadElapsed = Date.now() - uploadStartedAt;
              if (event.total && event.total > 0) {
                const pct = Math.min(100, Math.round((event.loaded / event.total) * 100));
                const etaMs =
                  event.loaded > 0 && pct < 100
                    ? Math.round((uploadElapsed * (event.total - event.loaded)) / event.loaded)
                    : undefined;
                setSendProgress({
                  phase: pct >= 100 ? 'preparing' : 'uploading',
                  percent: pct,
                  detail:
                    pct >= 100
                      ? `${attachments.length} file(s) uploaded`
                      : formatFileSizeProgress(event.loaded, event.total),
                  fileCount: attachments.length,
                  bytesLoaded: event.loaded,
                  bytesTotal: event.total,
                  elapsedMs: elapsed(),
                  etaMs,
                  tip:
                    pct >= 100
                      ? 'Files staged on the server — preparing the send request.'
                      : 'Uploading attachment binaries to the server.',
                });
              } else {
                setSendProgress({
                  phase: 'uploading',
                  percent: null,
                  detail: 'Uploading files…',
                  fileCount: attachments.length,
                  bytesLoaded: event.loaded,
                  elapsedMs: elapsed(),
                  tip: 'Upload size not reported by the browser — progress may stay indeterminate.',
                });
              }
            },
          });

          attachmentsPayload = (stageRes.data.attachments ?? []).map((att) => ({
            filename: att.filename,
            path: att.path,
            contentType: att.contentType,
          }));
        } else {
          let completedBytes = 0;
          attachmentsPayload = [];
          for (let i = 0; i < attachments.length; i++) {
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            const file = attachments[i].file;
            const encoded = await fileToBase64SendAttachment(
              file,
              (ratio) => {
                const done = completedBytes + file.size * ratio;
                const pct = Math.min(99, Math.round((done / totalBytes) * 100));
                const elapsedMs = elapsed();
                const etaMs =
                  done > 0 && pct < 100
                    ? Math.round((elapsedMs * (totalBytes - done)) / done)
                    : undefined;
                setSendProgress({
                  phase: 'preparing',
                  percent: pct,
                  detail: `${i + 1} of ${attachments.length}`,
                  fileName: file.name,
                  fileIndex: i + 1,
                  fileCount: attachments.length,
                  bytesLoaded: Math.round(done),
                  bytesTotal: totalBytes,
                  elapsedMs,
                  etaMs,
                  tip: 'Reading and encoding each file for transfer.',
                });
              },
              signal,
            );
            completedBytes += file.size;
            attachmentsPayload.push(encoded);
          }
          setSendProgress({
            phase: 'preparing',
            percent: 100,
            detail: `${attachments.length} file(s) ready`,
            fileCount: attachments.length,
            bytesLoaded: totalBytes,
            bytesTotal: totalBytes,
            elapsedMs: elapsed(),
            tip: 'Attachments ready — starting upload.',
          });
        }
      }

      // Use live editor HTML (same as draft save) so Enter/returns are not lost from a stale composeDoc.
      const docForSend = resolveComposeDocForPersistence();
      const outboundForSend = composeDocToOutbound(docForSend, {
        includeSignature: true,
        includeQuoted: true,
      });
      const sendText = outboundForSend.text;
      const sendHtml = outboundForSend.html || sendText.replace(/\n/g, '<br>');
      const threadPq = emailPartitionAtSearch(threadPartitionAt ?? originalEmail?.partitionAt);

      const hasAttachments = Boolean(attachmentsPayload?.length);
      const attachmentCount = attachmentsPayload?.length ?? 0;
      const attachmentBytes = attachments.reduce((sum, a) => sum + (a.file.size || 0), 0);
      const sendTimeout = hasAttachments
        ? applicationSettings?.emailSendWithAttachmentsTimeout ??
          APP_CONFIG.api.emailSendWithAttachmentsTimeout
        : applicationSettings?.emailSendTimeout ?? APP_CONFIG.api.emailSendTimeout;

      setSendProgress({
        phase: 'uploading',
        percent: 0,
        detail: hasAttachments
          ? useMultipart
            ? 'Sending message…'
            : 'Starting upload…'
          : 'Sending…',
        fileCount: hasAttachments ? attachmentCount : undefined,
        bytesLoaded: 0,
        bytesTotal: undefined,
        elapsedMs: elapsed(),
        tip: hasAttachments
          ? useMultipart
            ? `Sending message metadata for ${attachmentCount} staged attachment${attachmentCount === 1 ? '' : 's'} (${formatFileSize(attachmentBytes)}).`
            : `Uploading message plus ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'} (${formatFileSize(attachmentBytes)}).`
          : 'Uploading your message to the server.',
      });

      let uploadStartedAt: number | null = null;
      const requestConfig = {
        signal,
        timeout: sendTimeout,
        onUploadProgress: (event: { loaded: number; total?: number }) => {
          if (uploadStartedAt == null) uploadStartedAt = Date.now();
          const uploadElapsed = Date.now() - uploadStartedAt;
          if (event.total && event.total > 0) {
            const pct = Math.min(100, Math.round((event.loaded / event.total) * 100));
            const etaMs =
              event.loaded > 0 && pct < 100
                ? Math.round((uploadElapsed * (event.total - event.loaded)) / event.loaded)
                : undefined;
            if (pct >= 100) {
              setSendProgress({
                phase: 'sending',
                percent: 100,
                detail: 'Waiting for mail server…',
                fileCount: hasAttachments ? attachmentCount : undefined,
                bytesLoaded: event.total,
                bytesTotal: event.total,
                elapsedMs: elapsed(),
                tip: hasAttachments
                  ? 'Upload finished. The server is now sending through SMTP — this can take longer with large files.'
                  : 'Upload finished. Waiting for the mail server to accept the message.',
              });
            } else {
              setSendProgress({
                phase: 'uploading',
                percent: pct,
                detail: formatFileSizeProgress(event.loaded, event.total),
                fileCount: hasAttachments ? attachmentCount : undefined,
                bytesLoaded: event.loaded,
                bytesTotal: event.total,
                elapsedMs: elapsed(),
                etaMs,
                tip: hasAttachments
                  ? useMultipart
                    ? 'Transferring the send request (attachments already uploaded).'
                    : `Transferring encoded attachments (~${formatFileSize(attachmentBytes)} original size).`
                  : 'Transferring your message.',
              });
            }
          } else {
            setSendProgress({
              phase: 'uploading',
              percent: null,
              detail: 'Uploading…',
              fileCount: hasAttachments ? attachmentCount : undefined,
              bytesLoaded: event.loaded,
              elapsedMs: elapsed(),
              tip: 'Upload size not reported by the browser — progress may stay indeterminate.',
            });
          }
        },
      };

      if (isReply && id) {
        await api.post(
          `/emails/${id}/reply${threadPq}`,
          {
            accountId: formData.accountId,
            to: toArray.length > 0 ? toArray : undefined,
            ...(ccArray ? { cc: ccArray } : {}),
            text: sendText,
            html: sendHtml,
            fullBody: true,
            attachments: attachmentsPayload,
          },
          requestConfig,
        );
        showSuccess('Reply sent successfully!');
      } else if (isForward && id) {
        await api.post(
          `/emails/${id}/forward${threadPq}`,
          {
            accountId: formData.accountId,
            to: toArray,
            ...(ccArray ? { cc: ccArray } : {}),
            ...(bccArray ? { bcc: bccArray } : {}),
            text: sendText,
            html: sendHtml,
            fullBody: true,
            attachments: attachmentsPayload,
          },
          requestConfig,
        );
        showSuccess('Email forwarded successfully!');
      } else {
        const sendRes = await api.post<{
          queued?: boolean;
          undoDelaySeconds?: number;
          scheduledSendAt?: string | null;
        }>(
          '/emails/send',
          {
            accountId: formData.accountId,
            sendIdentityId: formData.sendIdentityId,
            to: toArray,
            ...(ccArray ? { cc: ccArray } : {}),
            ...(bccArray ? { bcc: bccArray } : {}),
            subject: formData.subject,
            text: sendText,
            html: sendHtml,
            attachments: attachmentsPayload,
            ...(scheduledSendAt ? { scheduledSendAt } : {}),
          },
          requestConfig,
        );
        const scheduledAt = sendRes.data?.scheduledSendAt;
        if (scheduledAt) {
          showSuccess(outboxScheduledSuccessMessage(scheduledAt));
        } else {
          const undoDelay = sendRes.data?.undoDelaySeconds ?? 0;
          if (sendRes.data?.queued && undoDelay > 0) {
            showSuccess(outboxQueuedSuccessMessage(undoDelay));
          } else if (sendRes.data?.queued) {
            showSuccess(outboxQueuedSuccessMessage(0));
          } else {
            showSuccess('Email sent successfully!');
          }
        }
      }
      void revalidateOutboxCaches();
      try {
        localStorage.removeItem(COMPOSE_DRAFT_KEY);
      } catch {
        // ignore
      }
      if (!isReply && !isForward) {
        if (applicationSettings?.emailKeepDraftAfterSend) {
          await keepPersistedDraftAfterSend();
        } else {
          await clearPersistedDraft();
        }
      }
      setShowScheduleSendModal(false);
      allowUnloadRef.current = true;
      exitCompose(navigate, location, { state: { fromComposeSuccess: true } });
    } catch (error: unknown) {
      const cancelled =
        (axios.isAxiosError(error) &&
          (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')) ||
        (error instanceof DOMException && error.name === 'AbortError') ||
        (error instanceof Error && error.name === 'AbortError');
      if (cancelled) {
        setSendError(null);
        return;
      }
      console.error('Error sending email:', error);
      let message: string | undefined;
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          message =
            attachments.length > 0
              ? 'Send timed out while uploading or delivering attachments. Please try again (or use a smaller attachment).'
              : 'Send timed out. Please try again.';
        } else {
          message = error.response?.data?.message;
        }
      } else if (error && typeof error === 'object' && 'response' in error) {
        message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
      }
      const fallback = 'Failed to send email. Please try again.';
      setSendError(message || fallback);
      showError(message || fallback);
    } finally {
      if (sendAbortRef.current === abortController) {
        sendAbortRef.current = null;
      }
      setIsSending(false);
      setSendProgress(null);
      setSendHasAttachments(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateBeforeSend()) return;
    await performSend();
  };

  const handleScheduleSendConfirm = async (scheduledSendAt: string) => {
    if (!validateBeforeSend()) return;
    await performSend(scheduledSendAt);
  };

  const saveDraftAndClose = useCallback(async () => {
    await saveDraftToStorage();
    setSendError(null);
    allowUnloadRef.current = true;
    exitCompose(navigate, location);
  }, [saveDraftToStorage, navigate, location]);

  const handleSaveAsDraft = useCallback(async () => {
    if (!hasContent || !formData.accountId || draftSaveInFlightRef.current) return;
    draftSaveInFlightRef.current = true;
    try {
      const ok = await enqueueComposeDraftSave(persistComposeDraft);
      if (!ok) {
        showError('Failed to save draft');
        return;
      }
      markComposeSaved();
      resetCountdown();
      showSuccess('Draft saved');
    } finally {
      draftSaveInFlightRef.current = false;
    }
  }, [hasContent, formData.accountId, persistComposeDraft, showSuccess, showError, markComposeSaved, resetCountdown]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (canOverwriteOpenedTemplate && openedLibraryTemplate?.id && openedLibraryTemplate.folderId != null) {
      setSavingLibraryTemplate(true);
      try {
        const templateName = formData.subject?.trim() || openedLibraryTemplate.name;
        const docForSave = buildComposeDocForTemplatePersistence({
          doc: composeDocRef.current,
          liveEditorHtml: editorRef.current?.getHtml?.(),
        });
        const templateBody = composeDocToOutbound(docForSave, { includeSignature: true, includeQuoted: true });
        const recipients = resolveRecipientsForPersistence();
        const updated = await updateTemplateApi(openedLibraryTemplate.id, {
          name: templateName,
          folderId: openedLibraryTemplate.folderId,
          accountId: formData.accountId || undefined,
          ...recipients,
          subject: formData.subject || undefined,
          html: templateBody.html || undefined,
          text: templateBody.text || undefined,
          bodyKind: docForSave.bodyKind,
          composeState: docForSave,
        });
        setOpenedLibraryTemplate(updated);
        applySavedRecipientsToForm(recipients);
        void mutateTemplateLibrary();
        showSuccess('Template updated');
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        showError(message || 'Failed to update template');
      } finally {
        setSavingLibraryTemplate(false);
      }
      return;
    }

    setSaveAsTemplateOpen(true);
  }, [
    canOverwriteOpenedTemplate,
    openedLibraryTemplate,
    formData,
    mutateTemplateLibrary,
    applySavedRecipientsToForm,
    showSuccess,
    showError,
    resolveComposeDocForPersistence,
    resolveRecipientsForPersistence,
  ]);

  return (
    <div className={cn('h-full flex flex-col min-h-0 overflow-hidden', colors.background)}>
      <input
        ref={messageFileInputRef}
        type="file"
        accept=".eml,.msg,.oft,message/rfc822"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleImportMessageFile(file);
        }}
      />
      <ComposeHeader
        colors={colors}
        backButtonRef={backButtonRef}
        onBack={confirmDiscard}
        presenceUsers={presenceUsers}
        showDraftStatus={periodicDraftAutosaveEnabled}
        draftAutosaveStatus={draftAutosaveStatus}
        secondsUntilSave={secondsUntilSave}
        canScheduleSend={canScheduleSend}
        isSending={isSending}
        sendProgress={sendProgress}
        sendHasAttachments={sendHasAttachments}
        onCancelSend={cancelSend}
        attachmentsOverLimit={attachmentsOverLimit}
        onSchedule={() => {
          if (!validateBeforeSend()) return;
          setShowScheduleSendModal(true);
        }}
        onAiMouseDown={captureAiSelection}
        onAi={toggleAiPanel}
        aiPanelOpen={aiPanelOpen}
        aiEmailsEnabled={enableAiEmails}
      />

      {!isReply && !isForward && !id && <ComposeMultiWindowHint colors={colors} />}

      {sendError && (
        <div className={cn('flex-shrink-0 border-b px-4 py-1.5 flex items-center justify-between gap-2', colors.border, colors.surface)}>
          <span className={cn('text-sm', colors.text)}>{sendError}</span>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setSendError(null); formRef.current?.requestSubmit(); }}>
              Retry
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={saveDraftAndClose}>
              Save as draft
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0 min-w-0 flex-row overflow-hidden">
      {!aiPanelOpen ? (
        <ComposeToolRail
          colors={colors}
          isImportingMessage={isImportingMessage}
          libraryOpen={openLibraryModal}
          addressBookOpen={addressBookOpen}
          aiPanelOpen={aiPanelOpen}
          aiEmailsEnabled={enableAiEmails}
          signaturesPanelOpen={signaturesPanelOpen}
          fileSendPanelOpen={fileSendPanelOpen}
          savingLibraryTemplate={savingLibraryTemplate}
          saveDraftDisabled={!hasContent}
          saveAsTemplateTitle={
            canOverwriteOpenedTemplate
              ? 'Update the opened library template'
              : 'Save message as a new library template'
          }
          onImportClick={openMessageFilePicker}
          onLibrary={() => setOpenLibraryModal(true)}
          onFileSendLink={() => setFileSendPanelOpen((v) => !v)}
          onToggleAddressBook={() => setAddressBookOpen((v) => !v)}
          onToggleSignatures={() => setSignaturesPanelOpen((v) => !v)}
          onSaveAsDraft={() => void handleSaveAsDraft()}
          onSaveAsTemplate={() => void handleSaveAsTemplate()}
          onPreview={() => setShowPreview(true)}
          onAiMouseDown={captureAiSelection}
          onAi={toggleAiPanel}
          onDiscard={confirmDiscard}
          canScheduleSend={canScheduleSend}
          onSchedule={() => {
            if (!validateBeforeSend()) return;
            setShowScheduleSendModal(true);
          }}
          scheduleDisabled={isSending || attachmentsOverLimit}
        />
      ) : null}

      <div className="flex flex-1 min-w-0 min-h-0 flex-row overflow-hidden">
        {addressBookOpen && (
          <ComposeAddressBookSidebar
            recentRecipients={recentRecipientsData ?? []}
            onPick={(recipient, field) => {
              const current = recipientsSnapshotRef.current;
              const nextValue =
                field === 'to'
                  ? appendRecipientToField(current.to, recipient)
                  : field === 'cc'
                    ? appendRecipientToField(current.cc, recipient)
                    : appendRecipientToField(current.bcc, recipient);
              if (field === 'to') {
                applyRecipientFields({ to: nextValue });
                setTouchedTo(true);
              } else if (field === 'cc') {
                applyRecipientFields({ cc: nextValue });
              } else {
                applyRecipientFields({ bcc: nextValue });
              }
            }}
            onClose={() => setAddressBookOpen(false)}
          />
        )}

        {fileSendPanelOpen && (
          <ComposeFileSendSidebar
            isOpen={fileSendPanelOpen}
            onClose={() => setFileSendPanelOpen(false)}
            onInsert={(url, filename) => {
              if (!editorRef.current) return;
              const linkHtml = `<p><a href="${url}" target="_blank" rel="noopener noreferrer">🔒 ${filename || 'Secure File Link'}</a></p>`;
              editorRef.current.insertHtml(linkHtml);
            }}
          />
        )}

        {signaturesPanelOpen && (
          <ComposeSignaturesSidebar
            options={signatureMenuOptions}
            onInsertSignature={handleInsertSignature}
            onManageSignatures={() => setShowSignaturesModal(true)}
            onEditReplyForwardIntro={() => {
              setReplyForwardIntroDraft(getReplyForwardIntroRawForEdit());
              setShowReplyForwardIntroModal(true);
            }}
            showReplyForwardIntro={isReply || isForward}
            onClose={() => setSignaturesPanelOpen(false)}
          />
        )}

        {aiPanelOpen ? (
          <EmailAiAssistant
            context="compose"
            presentation="inline"
            open={aiPanelOpen}
            onClose={() => setAiPanelOpen(false)}
            subject={formData.subject}
            body={formData.html}
            getBody={() => editorRef.current?.getHtml?.() ?? ''}
            getSelectedText={getSelectedTextForAi}
            emailId={aiThreadEmailId}
            partitionAt={aiThreadPartitionAt}
            clientId={aiThreadClientId}
            composeSessionId={composeSessionIdRef.current}
            onReplaceBody={(html) => {
              const userBody = stripLockedComposeBlocksFromUserBody(
                trimTrailingEmptyComposeParagraphs(html, { maxTrailingEmpty: 1 }),
              );
              applyComposeDoc({
                ...composeDocRef.current,
                bodyKind: 'rich',
                userBody,
              });
            }}
            onInsertAtCursor={(html) => {
              editorRef.current?.insertHtml(html);
              const updated = editorRef.current?.getHtml?.();
              if (updated) syncComposeUserBodyHtml(updated);
            }}
            onBumpEditorKey={() => setEditorContentKey((k) => k + 1)}
          />
        ) : null}

        <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
      {/* Form — scrollable so signature, editor, and forwarded message are all reachable */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden min-w-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex flex-col flex-1 min-h-0 w-full relative">
          {isComposeBusy && activeComposeLoading && (
            <div
              className={cn(
                'absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 rounded-xl',
                'bg-white/85 dark:bg-gray-900/85 backdrop-blur-sm',
              )}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <LoadingSpinner message={activeComposeLoading.message} size="medium" className="py-0" />
              {activeComposeLoading.progress != null && (
                <div className="w-56 max-w-[80%]">
                  <div className={cn('h-1.5 rounded-full overflow-hidden', colors.border, 'bg-gray-100 dark:bg-gray-800')}>
                    <div
                      className="h-full bg-primary transition-[width] duration-200 ease-out"
                      style={{ width: `${activeComposeLoading.progress}%` }}
                    />
                  </div>
                  <p className={cn('mt-1.5 text-center text-xs tabular-nums', colors.textMuted)}>
                    {activeComposeLoading.progress}%
                  </p>
                </div>
              )}
            </div>
          )}
          <form
            ref={formRef}
            id="compose-email-form"
            onSubmit={handleSubmit}
            className={cn('rounded-xl border shadow-sm overflow-hidden transition-opacity flex flex-col flex-1 min-h-0', colors.surface, colors.border)}
            style={{ opacity: isComposeBusy ? 0.55 : 1, pointerEvents: isComposeBusy ? 'none' : undefined }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                setSendError(null);
                formRef.current?.requestSubmit();
              }
            }}
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes('Files')) {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'copy';
                setAttachmentDragOver(true);
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setAttachmentDragOver(false);
              }
            }}
            onDrop={(e) => {
              if (!e.dataTransfer.types.includes('Files')) return;
              e.preventDefault();
              e.stopPropagation();
              setAttachmentDragOver(false);
              const files = e.dataTransfer.files;
              if (files?.length) addAttachmentFiles(Array.from(files));
            }}
          >
            {/* Recipients: From, To, Cc, Bcc */}
            <div className={cn('px-4 sm:px-5 py-1.5 border-b', colors.border)}>
              <div className="space-y-1.5">
                <RecipientInsetFieldShell label="From" colors={colors}>
                  <Select
                    required
                    value={formData.sendIdentityId != null ? formData.sendIdentityId.toString() : ''}
                    onChange={(e) => {
                      const selectedId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                      if (!selectedId) return;
                      const selected = fromIdentityOptions.find((o) => o.identityId === selectedId);
                      if (!selected) return;
                      setFormData((prev) => ({
                        ...prev,
                        accountId: selected.accountId,
                        sendIdentityId: selected.identityId,
                      }));
                    }}
                    className="w-full !border-0 !min-h-0 !bg-transparent !shadow-none !py-0"
                    options={
                      fromIdentityOptions.length > 0
                        ? fromIdentityOptions.map((o) => ({
                            value: o.identityId.toString(),
                            label: o.label,
                          }))
                        : [{ value: '', label: 'No send identities configured' }]
                    }
                  />
                </RecipientInsetFieldShell>
                <RecipientSelector
                  to={formData.to}
                  cc={formData.cc}
                  bcc={formData.bcc}
                  onChangeTo={(v) => {
                    applyRecipientFields({ to: v });
                    setTouchedTo(true);
                  }}
                  onChangeCc={handleChangeCc}
                  onChangeBcc={handleChangeBcc}
                  toInvalid={toInvalid}
                  showCc={showCc}
                  showBcc={showBcc}
                  onToggleCc={handleToggleCc}
                  onToggleBcc={handleToggleBcc}
                  placeholderTo={isReply ? 'Reply to' : isForward ? 'Forward to' : 'Add recipients'}
                  placeholderCc="Add recipients"
                  placeholderBcc="Add recipients"
                  ccBccLocation="belowSubject"
                  recipientSuggestions={recentRecipients}
                />
                {(showCc || showBcc) && (
                  <RecipientCcBccSection
                    to={formData.to}
                    cc={formData.cc}
                    bcc={formData.bcc}
                    onChangeTo={(v) => applyRecipientFields({ to: v })}
                    onChangeCc={handleChangeCc}
                    onChangeBcc={handleChangeBcc}
                    showCc={showCc}
                    showBcc={showBcc}
                    placeholderCc="Add recipients"
                    placeholderBcc="Add recipients"
                    recipientSuggestions={recentRecipients}
                  />
                )}
              </div>
            </div>

            {/* Subject */}
            <div className={cn('px-4 sm:px-5 py-1.5 border-b', colors.border)}>
              <div className={cn('min-h-[1.75rem] rounded border flex flex-wrap items-center gap-1 px-2 py-1 bg-gray-50/50 dark:bg-gray-800/30', colors.border, subjectInvalid && 'ring-2 ring-offset-0 ring-gray-400 dark:ring-gray-500')}>
                <input
                  ref={subjectInputRef}
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  onBlur={() => setTouchedSubject(true)}
                  placeholder="Subject"
                  className={cn('flex-1 min-w-[8rem] bg-transparent border-0 outline-none text-sm py-0.5 placeholder-gray-400 dark:placeholder-gray-500', colors.text)}
                  maxLength={SUBJECT_MAX_LENGTH}
                  aria-label="Subject"
                />
              </div>
            </div>

            {openedLibraryTemplate?.id ? (
              <div className={cn('px-4 sm:px-5 py-2 border-b', colors.border)}>
                <EntityCustomFieldsSection
                  entityType={CUSTOM_FIELD_ENTITY.EMAIL_TEMPLATE}
                  entityId={openedLibraryTemplate.id}
                  title="Template custom fields"
                  compact
                />
              </div>
            ) : null}

            {/* Attachments accordion */}
            <div
              className={cn(
                'border-b transition-all duration-200',
                colors.border,
                attachmentDragOver && 'bg-primary/10 dark:bg-primary/20 border-primary/40'
              )}
              onDragOver={handleAttachmentDragOver}
              onDragLeave={handleAttachmentDragLeave}
              onDrop={handleAttachmentDrop}
            >
              <button
                type="button"
                onClick={() => setAttachmentsExpanded((v) => !v)}
                className={cn(
                  'w-full px-4 sm:px-5 py-2 flex items-center justify-between gap-2 text-left',
                  colors.text,
                  'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors'
                )}
                aria-expanded={attachmentsExpanded}
              >
                <span className={cn('text-xs font-medium uppercase tracking-wider', colors.textMuted)}>
                  Attachments {attachments.length > 0 && `(${attachments.length})`}
                </span>
                {attachments.length > 0 && (
                  <span className={cn('text-xs', colors.textMuted)}>
                    {(totalAttachmentSize / 1024 / 1024).toFixed(2)} MB / {attachmentMaxSizeMb} MB
                  </span>
                )}
                <svg
                  className={cn('w-4 h-4 flex-shrink-0 transition-transform', colors.textMuted)}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ transform: attachmentsExpanded ? 'rotate(180deg)' : undefined }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {attachmentsExpanded && (
                <div className="px-4 sm:px-5 pb-3 pt-0 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <label
                      className={cn(
                        'relative flex-1 min-w-[140px] flex items-center justify-center gap-2 rounded-lg border border-dashed py-2 px-3 transition-colors cursor-pointer',
                        'focus-within:ring-2 focus-within:ring-offset-0 focus-within:ring-indigo-500/40',
                        attachmentDragOver
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : cn(colors.border, colors.surfaceHover, 'hover:border-gray-400 dark:hover:border-gray-500'),
                      )}
                    >
                      <svg className={cn('w-4 h-4 flex-shrink-0 pointer-events-none', attachmentDragOver ? 'text-primary' : colors.textMuted)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className={cn('text-xs font-medium pointer-events-none', attachmentDragOver ? 'text-primary' : colors.text)}>
                        {attachmentDragOver ? 'Drop to attach' : 'Drop files or click to add'}
                      </span>
                      {/* Not display:none — some Chrome builds ignore a hidden file input, including label activation. */}
                      <input
                        type="file"
                        multiple
                        aria-label="Add attachments"
                        className="absolute inset-0 z-10 block h-full w-full cursor-pointer opacity-0 text-[200px] leading-none"
                        onChange={(e) => {
                          const files = e.target.files;
                          const list = files?.length ? Array.from(files) : [];
                          e.target.value = '';
                          if (list.length) addAttachmentFiles(list);
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setLibraryAttachOpen(true)}
                      className={cn(
                        'flex items-center justify-center gap-2 rounded-lg border py-2 px-3 text-xs font-medium transition-colors',
                        colors.border,
                        colors.surfaceHover,
                        colors.text,
                      )}
                    >
                      From library
                    </button>
                    {APP_CONFIG.features.fileSend && (
                      <button
                        type="button"
                        onClick={() => {
                          window.open(
                            '/file-send?compose=1',
                            'ucomm-file-send',
                            'noopener,noreferrer,width=560,height=720',
                          );
                        }}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-lg border py-2 px-3 text-xs font-medium transition-colors',
                          colors.border,
                          colors.surfaceHover,
                          colors.text,
                        )}
                        title="Encrypt and upload a large file, then insert a download link"
                      >
                        Send as encrypted link
                      </button>
                    )}
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {attachments.map((a) => (
                        <div
                          key={a.id}
                          className={cn(
                            'inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-md border text-xs max-w-[min(100%,280px)] min-w-0',
                            colors.surface,
                            colors.border
                          )}
                        >
                          <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openComposeAttachment(a.file);
                            }}
                            className={cn(
                              'min-w-0 flex-1 text-left truncate font-medium underline-offset-2 hover:underline',
                              colors.text,
                              colors.interactiveHover
                            )}
                            title={`Open ${a.name}`}
                          >
                            {a.name}
                          </button>
                          <span className={cn('text-[10px] flex-shrink-0', colors.textMuted)}>
                            {formatFileSize(a.size)}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openComposeAttachment(a.file);
                            }}
                            className={cn(
                              'flex-shrink-0 p-0.5 rounded opacity-70 hover:opacity-100',
                              colors.textMuted,
                              colors.interactiveHover,
                              'transition-colors'
                            )}
                            title={`Open ${a.name} in a new tab`}
                            aria-label={`Open ${a.name}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setAttachments((prev) => prev.filter((x) => x.id !== a.id)); }}
                            className={cn(
                              'flex-shrink-0 p-0.5 rounded opacity-70 hover:opacity-100',
                              'text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
                              'transition-colors'
                            )}
                            aria-label={`Remove ${a.name}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Message body - fills remaining height */}
            <div className={cn('flex-1 flex flex-col min-h-0 px-4 sm:px-5 py-2', colors.surface)}>
              <div className={cn('relative flex-1 flex flex-col min-h-0 rounded-xl border transition-shadow focus-within:ring-2 focus-within:ring-offset-0 focus-within:ring-indigo-500/50', colors.border)}>
                <div className="flex-1 flex flex-col min-h-0 p-3 overflow-hidden">
                  <div className={cn('flex flex-col w-full min-h-0 flex-1')}>
                    <SlateEmailEditor
                      ref={editorRef}
                      key={editorContentKey}
                      className={composeDoc.quoted ? 'flex-1 min-h-0' : undefined}
                      coalescePlainTextBlocks={false}
                      value={composeDoc.userBody}
                      onChange={(html) => {
                        syncComposeUserBodyHtml(html);
                      }}
                      placeholder="Write your message…"
                      contentKey={editorContentKey}
                      fillHeight={!composeDoc.quoted}
                      compactUserZone={Boolean(composeDoc.quoted)}
                      stickyToolbar
                      minHeight={composeDoc.quoted ? undefined : editorHeight}
                      colors={colors as unknown as Record<string, string>}
                      toolbarConfig={{ onError: showError }}
                      onInsertImage={() => setImageDialogOpen(true)}
                      belowEditor={
                        composeDoc.signature ? (
                          <div
                            className="px-3 py-3 email-signature email-signature-preview select-text"
                            dangerouslySetInnerHTML={{ __html: sanitizeRichEmailHtml(composeDoc.signature) }}
                          />
                        ) : undefined
                      }
                      scrollFooter={
                        composeDoc.quoted ? (
                          <QuotedBodyPanel
                            quoted={composeDoc.quoted}
                            emailId={originalEmail?.id}
                            partitionAt={threadPartitionAt ?? originalEmail?.partitionAt ?? undefined}
                            colors={colors as unknown as Record<string, string>}
                            collapsedByDefault={isForward}
                            className="mt-4 flex-shrink-0 px-3 pb-3"
                            onQuotedChange={(quoted) => {
                              const next = { ...composeDocRef.current, quoted };
                              setComposeDoc(next);
                              setFormData((fd) => ({ ...fd, html: composeDocToEditorHtml(next) }));
                            }}
                          />
                        ) : undefined
                      }
                    />
                  </div>
                </div>
                <div
                  role="separator"
                  aria-label="Resize message area"
                  onMouseDown={onResizeStart}
                  className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center border-t border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <span className="w-8 h-1 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden />
                </div>
              </div>
            </div>

            {/* Footer */}
            <footer className={cn('px-4 sm:px-5 py-1 flex items-center border-t', colors.border, colors.background)}>
              <span className={cn('text-xs', colors.textMuted)}>
                {wordCount} words · {plainText.length} characters
              </span>
              {periodicDraftAutosaveEnabled && (
                <span className={cn('sm:hidden ml-3 text-xs truncate', colors.textMuted)} aria-live="polite">
                  {draftAutosaveStatus === 'saving'
                    ? 'Saving draft…'
                    : draftAutosaveStatus === 'error'
                      ? 'Could not save draft'
                      : `Draft in ${formatDraftAutosaveCountdown(secondsUntilSave)}`}
                </span>
              )}
            </footer>
          </form>
        </div>
      </div>
        </div>
      </div>
      </div>

      <Modal isOpen={showReplyForwardIntroModal} onClose={() => setShowReplyForwardIntroModal(false)} size="lg">
        <ModalHeader>
          <ModalTitle>Message before signature</ModalTitle>
          <button
            type="button"
            onClick={() => setShowReplyForwardIntroModal(false)}
            className={cn('rounded-lg p-1.5', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm mb-3', colors.textMuted)}>
            Default HTML placed above your signature in <strong className={colors.text}>reply</strong> and{' '}
            <strong className={colors.text}>forward</strong>. Stored in this browser. Use simple tags such as{' '}
            <code className="text-xs">&lt;p&gt;</code>, <code className="text-xs">&lt;br&gt;</code>,{' '}
            <code className="text-xs">&lt;strong&gt;</code>.
          </p>
          <Textarea
            rows={8}
            value={replyForwardIntroDraft}
            onChange={(e) => setReplyForwardIntroDraft(e.target.value)}
            className={cn('w-full font-mono text-sm', colors.text)}
            aria-label="HTML before signature"
          />
        </ModalContent>
        <ModalFooter className="flex-wrap gap-2 justify-between sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              resetReplyForwardIntroHtml();
              setReplyForwardIntroDraft(DEFAULT_REPLY_FORWARD_INTRO_HTML);
            }}
          >
            Reset to default
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowReplyForwardIntroModal(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setReplyForwardIntroHtml(replyForwardIntroDraft);
                setFormData((prev) => ({
                  ...prev,
                  html: replaceReplyForwardIntroInBody(prev.html, replyForwardIntroDraft),
                }));
                setEditorContentKey((k) => k + 1);
                setShowReplyForwardIntroModal(false);
                showSuccess('Saved and applied to this message.');
              }}
            >
              Save &amp; apply to this message
            </Button>
            <Button
              type="button"
              variant="default"
              className={cn(colors.primary, colors.primaryText, colors.primaryHover)}
              onClick={() => {
                setReplyForwardIntroHtml(replyForwardIntroDraft);
                setShowReplyForwardIntroModal(false);
                showSuccess('Default saved for new replies and forwards.');
              }}
            >
              Save default
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <ComposeScheduleSendModal
        isOpen={showScheduleSendModal}
        onClose={() => setShowScheduleSendModal(false)}
        onConfirm={handleScheduleSendConfirm}
        isSubmitting={isSending}
      />

      <SaveAsTemplateModal
        isOpen={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        canSaveCompany={canEditCompanyTemplates}
        personalFolders={templatePersonalLibrary.folders}
        companyFolders={templateCompanyLibrary.folders}
        getTemplateBodyForSave={getTemplateBodyForSave}
        getRecipientsForSave={resolveRecipientsForPersistence}
        formData={{
          accountId: formData.accountId || undefined,
          subject: formData.subject || undefined,
        }}
        onSaved={() => {
          void mutateTemplateLibrary();
          markComposeSaved();
          showSuccess('Template saved');
        }}
      />

      <OpenTemplateLibraryModal
        isOpen={openLibraryModal}
        onClose={() => setOpenLibraryModal(false)}
        onOpenTemplate={handlePickLibraryTemplate}
        companyTemplates={templateCompanyLibrary.templates}
        personalTemplates={templatePersonalLibrary.templates}
        sharedTemplates={templateSharedLibrary.templates}
        companyFolders={templateCompanyLibrary.folders}
        personalFolders={templatePersonalLibrary.folders}
        sharedFolders={templateSharedLibrary.folders}
        companyCanEdit={templateCompanyLibrary.canEdit}
        companyDocuments={templateCompanyLibrary.documents}
        personalDocuments={templatePersonalLibrary.documents}
        isLoading={templateLibraryLoading}
      />

      <ConfirmationDialog
        isOpen={libraryReplaceConfirm != null}
        onClose={() => setLibraryReplaceConfirm(null)}
        onConfirm={() => {
          if (libraryReplaceConfirm) void enrichAndLoadLibraryTemplate(libraryReplaceConfirm);
          setLibraryReplaceConfirm(null);
        }}
        title="Replace current message?"
        message="You have unsaved changes. Opening this template will replace the current content."
        confirmText="Replace"
        cancelText="Keep editing"
        confirmVariant="danger"
      />

      <AttachFromTemplateLibraryModal
        isOpen={libraryAttachOpen}
        onClose={() => setLibraryAttachOpen(false)}
        onAttach={handleAttachFromLibrary}
        companyDocuments={templateCompanyLibrary.documents ?? []}
        personalDocuments={templatePersonalLibrary.documents ?? []}
        companyFolders={templateCompanyLibrary.folders}
        personalFolders={templatePersonalLibrary.folders}
        companyCanEdit={templateCompanyLibrary.canEdit}
        isLoading={templateLibraryLoading}
      />

      <ManageSignaturesModal
        isOpen={showSignaturesModal}
        onClose={() => setShowSignaturesModal(false)}
        account={(() => {
          const acc = accountsWithSigs.find((a) => a.id === formData.accountId);
          return acc ? { id: acc.id, emailAddress: acc.emailAddress } : null;
        })()}
        signatures={signaturesByAccount[formData.accountId]?.signatures ?? []}
      />

      <ConfirmationDialog
        isOpen={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={doDiscard}
        title="Discard draft"
        message="Your message will be lost. Are you sure you want to discard?"
        confirmText="Discard"
        cancelText="Keep editing"
        confirmVariant="danger"
      />

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPreview(false)} aria-hidden />
          <div className={cn('relative flex flex-col rounded-xl border shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden', colors.surface, colors.border)}>
            <div className={cn('flex items-center justify-between px-4 py-3 border-b', colors.border)}>
              <h3 className={cn('text-sm font-semibold', colors.text)}>Preview</h3>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(false)}>Close</Button>
            </div>
            <div className={cn('flex-1 overflow-y-auto p-4 space-y-3', colors.text)}>
              <div>
                <span className={cn('text-xs font-medium uppercase', colors.textMuted)}>To: </span>
                <span>{formData.to || '(none)'}</span>
              </div>
              {(formData.cc || formData.bcc) && (
                <div>
                  {formData.cc && <><span className={cn('text-xs font-medium uppercase', colors.textMuted)}>Cc: </span><span>{formData.cc}</span></>}
                  {formData.bcc && <><span className={cn('text-xs font-medium uppercase', colors.textMuted)}>Bcc: </span><span>{formData.bcc}</span></>}
                </div>
              )}
              <div>
                <span className={cn('text-xs font-medium uppercase', colors.textMuted)}>Subject: </span>
                <span className="font-medium">{formData.subject || '(no subject)'}</span>
              </div>
              <div className={cn('border-t pt-3 mt-3', colors.border)}>
                <div
                  className={cn('prose prose-sm max-w-none px-4 py-3 [&_p]:my-2 [&_div]:my-2', EMAIL_MESSAGE_CANVAS_CLASS)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      expandComposeEditorHtmlForDisplay(
                        composeDocToOutbound(resolveComposeDocForPersistence(), {
                          includeSignature: true,
                          includeQuoted: true,
                        }).html || '',
                      ),
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insert image from URL modal */}
      {imageDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setImageDialogOpen(false); setImageUrl(''); setImageError(''); }} aria-hidden />
          <div className={cn('relative w-full max-w-sm rounded-xl border shadow-xl p-4', colors.surface, colors.border)}>
            <h3 className={cn('text-sm font-semibold mb-3', colors.text)}>Insert image from URL</h3>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => { setImageUrl(e.target.value); setImageError(''); }}
              placeholder="https://..."
              className={cn('w-full px-3 py-2 text-sm border rounded-lg mb-3', colors.border, 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const u = imageUrl.trim();
                  if (u) editorRef.current?.insertImage(u);
                  setImageDialogOpen(false);
                  setImageUrl('');
                }
              }}
            />
            {imageError && <p className={cn('text-xs mb-2', colors.textMuted)}>{imageError}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setImageDialogOpen(false); setImageUrl(''); setImageError(''); }}>Cancel</Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const url = imageUrl.trim();
                  if (!url) {
                    setImageError('Enter an image URL');
                    return;
                  }
                  editorRef.current?.insertImage(url);
                  setImageDialogOpen(false);
                  setImageUrl('');
                  setImageError('');
                }}
              >
                Insert
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compose;
